import { Injectable, Optional, inject, PLATFORM_ID } from '@angular/core';
import {
  FetchPunksmarketBidsParams,
  PunksmarketBidIndexerService,
} from './punksmarket-bid-indexer.service';
import { HttpClient } from '@angular/common/http';
import { isPlatformServer } from '@angular/common';

import { environment } from 'src/environments/environment';
import CigABI from '../../abi/Cig.json';

import { Observable, of, BehaviorSubject, EMPTY, forkJoin, timer, firstValueFrom, combineLatest, from, interval } from 'rxjs';
import { catchError, filter, map, pairwise, startWith, switchMap, take, tap, timeout, withLatestFrom } from 'rxjs/operators';

import { Apollo, WatchQueryOptions, gql } from 'apollo-angular';
import { ApolloQueryResult, QueryOptions } from '@apollo/client';

import { Attribute, Auction, Event, EventType, Listing, Punk, State } from '@/models/graph';
import { Period, PeriodKey, StateStats, SubgraphSortDirections, SubgraphSorts } from '@/models/pipes';

import { StateService } from './state.service';

import { WeiToEthPipe } from '@/pipes/wei-to-eth.pipe';
import { filterData } from '@/constants/filterData';
import {GraphQLQueriesService} from "@/services/graphql-queries.service";
import { PUNKAUCTION_ADDRESS, TOKENWORKS_ADDRESS, ZERO_ADDRESS } from '@/constants/utils';
import {
  isPunksmarketRouterAddress,
  PUNKSMARKET_V1_ROUTER_ADDRESS,
} from '@/constants/punksmarket-v1';
import { encodeFunctionData, formatEther, parseEther } from 'viem';
import { EthFollowStats } from '@/models/eth-follows';
import { PlatformService } from './platform.service';
import {
  buildDashboardSaleCollections,
  isDashboardSaleWrapped,
  isOwnedPunkWrapped,
  mapIndexerListedTokenToListing,
  mapMarketplaceIndexerListingToListing,
  mergeOwnedListings,
} from '@/utils/dashboard-sale.util';

const pollInterval = 30000;
const fetchTimer = 30000;

const WATCH_STATES_DATA = gql`
  query GetStates($first: Int, $lastTimestamp: BigInt, $nowTimestamp: BigInt) {
    states(
      first: $first,
      where: { timestamp_gt: $lastTimestamp, timestamp_lt: $nowTimestamp },
      orderBy: "timestamp",
      orderDirection: desc,
    ) {
      id
      timestamp
      usd
      volume
      floor
      topSale {
        tokenId
        value
      }
      sales
      owners
      topBid {
        tokenId
        value
      }
      bids
    }
  }
`;

const GET_USER_SENT_SALES = gql`
  query GetSentEvents($limit: Int!, $address: String!) {
    events(
      orderBy: "blockTimestamp",
      orderDirection: desc,
      first: $limit,
      where: {
        fromAccount_: {
          id: $address
        }
        type: "Sale"
        value_gt: 0
      }
    ) {
      type
      tokenId
      platform
      blockTimestamp
      value
      usd
      transactionHash
      fromAccount {
        id
      }
      toAccount {
        id
      }
    }
  }
`;

const GET_USER_RECEIVED_SALES = gql`
  query GetReceivedEvents($limit: Int!, $address: String!) {
    events(
      orderBy: "blockTimestamp",
      orderDirection: desc,
      first: $limit,
      where: {
        toAccount_: {
          id: $address
        }
        type: "Sale"
        value_gt: 0
      }
    ) {
      type
      tokenId
      blockTimestamp
      value
      platform
      transactionHash
      usd
      fromAccount {
        id
      }
      toAccount {
        id
      }
    }
  }
`;

const GET_USER_LISTINGS = gql`
  query GetListings($limit: Int!, $address: String!) {
    listings(
      orderBy: "blockTimestamp",
      orderDirection: desc,
      first: $limit,
      where: {
        fromAccount_: {
          id: $address
        }
      }
    ) {
      id
      value
      blockTimestamp
    }
  }
`;

const GET_LISTED_TOKENS = gql`
  query GetListedTokens($collections: [String!]!, $tokenIds: [String!]) {
    getListedTokens(collections: $collections, tokenIds: $tokenIds) {
      tokenId
      collection
      offeredDate
      amount
    }
  }
`;

const GET_BIDS_FOR_TOKENS = gql`
  query GetBidsForTokens {
    bids(
      first: 1000
      orderBy: blockTimestamp
      orderDirection: desc
    ) {
      id
      value
      transactionHash
      blockTimestamp
      fromAccount {
        id
      }
      punk {
        id
      }
    }
  }
`;

const GET_USER_BIDS = gql`
  query GetBids($limit: Int!, $address: String!) {
    bids(
      orderBy: "blockTimestamp",
      orderDirection: desc,
      first: $limit,
      where: {
        fromAccount_: {
          id: $address
        }
      }
    ) {
      id
      value
      blockTimestamp
      usd
    }
  }
`;

// TODO readd platform here when new graph released
const GET_EVENTS_TYPE = gql`
  query GetEvents($limit: Int!, $skip: Int!, $type: String!, $toAccountNot: String!, $minValue: Int!, $order: String!, $orderDirection: String!) {
    events(
      orderBy: $order,
      orderDirection: $orderDirection,
      first: $limit,
      skip: $skip,
      where: { type: $type, toAccount_not: $toAccountNot, value_gt: $minValue }
    ) {
      id
      type
      tokenId
      blockTimestamp
      transactionHash
      value
      usd
      platform
      toAccount {
        id
      }
      fromAccount {
        id
      }
    }
  }
`;
const GET_GONDI_LISTINGS = gql`
query ListNFTsForBuy($collections: [Int!]!, $currencyAddresses: [Address!], $onlyListed: Boolean, $marketplaces: [MarketPlaceType!], $sortBy: [NftSortInput!]) {
  nfts: listNftsFromCollections(
    collections: $collections
    currencyAddresses: $currencyAddresses
    onlyListed: $onlyListed
    marketplaces: $marketplaces
    sortBy: $sortBy
  ) {
    totalCount
    pageInfo {
      endCursor
      hasNextPage
    }
    edges {
      node {
        tokenId,
        collection {
          id,
          slug
        },
        nftPriceSample {
          currencyAddress
          value
        }
      }
    }
  }
}
`

const GET_NFTX_VAULT = gql`
  query GetNftxVault($contract:String!, $limit: Int!) {
    vault(
      id: $contract
    ) {
      holdings(
        orderBy: dateAdded,
        orderDirection: desc,
        first: $limit,
      ) {
        tokenId
        dateAdded
      }
    }
  }
`;

const GET_SIGNLE_NFTX_VAULT = gql`
  query GetNftxVault($contract:String!, $punkId: String!) {
    vault(
      id: $contract
    ) {
      holdings(where: { tokenId: $punkId }) {
        tokenId
        dateAdded
      }
    }
  }
`;

const GET_CIG_CLAIM = gql`
  query GetCigClaim($punkIndex: String!) {
    claims(where: {punkIndex: $punkIndex}) {
      id
      owner
      punkIndex
      value
      transactionHash
      blockTimestamp
      blockNumber
    }
  }
`;

export interface ExternalOrderbookBidPublication {
  source: string;
  orderbook_bid_id: string;
  bidder_address: string;
  bid_nonce: number;
  chain_id: number;
  stash_contract: string;
  auction_contract: string;
  bid_amount_wei: string;
  punk_indices_json: string;
  tag: string | null;
  createdAt: string;
}

export interface ExternalOrderbookBidPublicationsPage {
  items: ExternalOrderbookBidPublication[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface StashBidProtectionLogRow {
  id: string;
  reason: string;
  status: string;
  protected_bid_id: string;
  protected_source: string | null;
  legacy_punk_index: number;
  legacy_bidder_address: string;
  legacy_value_wei: string;
  legacy_tx_hash: string;
  legacy_log_index: number;
  legacy_block_number: number | null;
  reset_tx_hash: string | null;
  error_message: string | null;
  created_at: string;
}

export interface StashBidProtectionLogsPage {
  enabled: boolean;
  items: StashBidProtectionLogRow[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

@Injectable({
  providedIn: 'root'
})
export class DataService {

  staticUrl = environment.staticUrl;
  tokenImagePath = environment.tokenImagePath;
  private attributesData!: any;

  private fetch = new BehaviorSubject<number>(0);
  fetch$ = this.fetch.asObservable();

  private statesData = new BehaviorSubject<State[]>([]);
  statesData$ = this.statesData.asObservable();
  statesStats$!: Observable<{ [key in PeriodKey]: { sales: number; volume: number } }>;

  private marketData = new BehaviorSubject<Punk[]>([]);
  marketData$ = this.marketData.asObservable();

  private salesData = new BehaviorSubject<Punk[]>([]);
  salesData$ = this.salesData.asObservable();

  getSalesDataValue(): Punk[] {
    return this.salesData.getValue();
  }

  private ownedData = new BehaviorSubject<Punk[]>([]);
  ownedData$ = this.ownedData.asObservable();

  private eventsData = new BehaviorSubject<Event[]>([]);
  eventsData$ = this.eventsData.asObservable();

  private currentFloor = new BehaviorSubject<number>(0);
  currentFloor$ = this.currentFloor.asObservable();

  private usd = new BehaviorSubject<number>(0);
  usd$ = this.usd.asObservable();

  private btc = new BehaviorSubject<number>(0);
  btc$ = this.btc.asObservable();

  filterData = filterData;

  attributes!: any;
  gondiListings: Map<string,Listing> = new Map<string, Listing>();

  private readonly punksmarketBidIndexer = inject(PunksmarketBidIndexerService);

  constructor(
    private http: HttpClient,
    private apollo: Apollo,
    private stateSvc: StateService,
    private weiToEthPipe: WeiToEthPipe,
    private graphQLQueriesService: GraphQLQueriesService,
    private platformService: PlatformService
  ) {
    console.log('[DataService] Constructor called, Apollo available:', !!this.apollo);
    console.log('[DataService] Apollo client available:', !!this.apollo?.client);
    console.log('[DataService] Platform isServer:', this.platformService.isServer);
    // Initialize with safe defaults
    this.currentFloor.next(0);
    this.usd.next(0);
    
    // Initialize statesData with default data on server to avoid timeout
    if (this.platformService.isServer) {
      console.log('[DataService] Initializing statesData with default data for SSR');
      this.statesData.next([
        {
          id: '2024-01-01',
          timestamp: '1704067200',
          usd: '2000',
          topBid: null as any,
          topSale: null as any,
          listings: '100',
          delistings: '10',
          bids: '50',
          sales: '25',
          owners: '1000',
          volume: '1000000000000000000000', // 1000 ETH
          floor: '10000000000000000000' // 10 ETH
        }
      ]);
    }
    
    // Initialize statesStats$ AFTER statesData is populated
    this.statesStats$ = this.getStatesStats();
    
    // getMarketData call on server AND client side
    console.log('[DataService] Appel getMarketData() (SSR et client)');
    this.getMarketData();
    
    // Start states watching for both SSR and client
    console.log('[DataService] Starting watchStates() (SSR et client)');
    this.watchStates().pipe(
      take(1), // Ends after first emission
      tap((res) => {
        console.log('[DataService] watchStates completed with', res?.length, 'states');
        if (res && res.length > 0 && res[0].floor && this.stateSvc.getAggregatedFloor() === 0) {
          this.stateSvc.setAggregatedFloor(this.weiToEthPipe.transform(res[0].floor));
        }
      }),
      catchError((error) => {
        console.error('[DataService] Error in watchStates:', error);
        return of([]);
      })
    ).subscribe(() => {
      console.log('[DataService] watchStates subscription completed');
    });
    
    // Only run these operations on the client side
    if (this.platformService.isClient) {
      // Start basic data fetching with error handling
      try {
        this.fetchUSDPrice();
        this.fetchBTCPrice();
        this.fetchNftxPrice()
          .pipe(
            catchError((error) => {
              console.error('[DataService] fetchNftxPrice error in init:', error);
              return of('0');
            })
          )
          .subscribe((price) => {
            console.log('[DataService] fetchNftxPrice subscribed value:', price);
          });
        // Start market data fetching
        this.getMarketData();
        // Start sales data subscription
        console.log('[DataService] Starting sales data subscription on client');
        this.getSalesData().pipe(
          tap((result) => console.log('[DataService] getSalesData subscription completed with:', result?.length || 0, 'items')),
          catchError((error) => {
            console.error('Error in sales data subscription:', error);
            return of([]);
          })
        ).subscribe();
        // Subscribe to wallet address changes with error handling
        this.stateSvc.walletAddress$.pipe(
          switchMap((res) => res ? this.fetchOwned(res) : of([])),
          tap((res) => this.setOwned(res)),
          catchError((error) => {
            console.error('Error in wallet address subscription:', error);
            return of([]);
          })
        ).subscribe();
        // Subscribe to marketplace filter changes with error handling
        this.stateSvc.activeMarketplaceFilters$
          .pipe(
            startWith(null),
            pairwise(),
            catchError((error) => {
              console.error('Error in marketplace filters subscription:', error);
              return of([null, null]);
            })
          )
          .subscribe(([previous, next]) => {
            console.log('[DataService] Marketplace filter changed from', previous, 'to', next);
            
            // Reload market data for all filter changes
            this.stateSvc.setMarketLoading(true);
            this.marketData.next([]);
            this.getMarketData();
            
            // Also reload sales data if we're on the sales page
            if (next) {
              console.log('[DataService] Reloading sales data for filter:', next);
              this.clearSalesData();
              this.getSalesData().pipe(
                take(1)
              ).subscribe();
            }
          });
        // Start states watching with error handling
        this.watchStates().pipe(
          tap((res) => {
            if (res && res.length > 0 && res[0].floor && this.stateSvc.getAggregatedFloor() === 0) {
              this.stateSvc.setAggregatedFloor(this.weiToEthPipe.transform(res[0].floor));
            }
          }),
          catchError((error) => {
            console.error('Error in states watching:', error);
            return of([]);
          })
        ).subscribe();
        // Start CIG price fetching with error handling
        this.fetchCigPrice().pipe(
          tap((res) => this.stateSvc.setCigBalances({ cigPrice: res.price, marketCapUSD: res.marketCap })),
          catchError((error) => {
            console.error('Error fetching CIG price:', error);
            return of({ price: 0, marketCap: 0 });
          })
        ).subscribe();
        // Start CIG CEO fetching with error handling
        this.fetchCigCEO().pipe(
          tap((res) => this.stateSvc.setCigCEO(res)),
          catchError((error) => {
            console.error('Error fetching CIG CEO:', error);
            return of(null);
          })
        ).subscribe();
      } catch (error) {
        console.error('Error in DataService initialization:', error);
      }
    }
  }

  triggerFetch() {
    this.fetch.next(new Date().getTime());
  }

  /**
   * Convert ETH value to Wei using BigInt for precision
   * @param ethValue - ETH value as string or number
   * @returns Wei value as string
   */
  private ethToWei(ethValue: string | number): string {
    if (!ethValue || ethValue === '0' || ethValue === 0) return '0';
    
    try {
      const ethStr = typeof ethValue === 'number' ? ethValue.toString() : ethValue;
      const weiBigInt = parseEther(ethStr);
      return weiBigInt.toString();
    } catch (error) {
      console.error('[DataService] Error converting ETH to Wei:', error, 'Value:', ethValue);
      return '0';
    }
  }

  /**
   * Test function to verify ETH to Wei conversion
   * This can be called from browser console for testing
   */
  testEthToWeiConversion(): void {
    console.log('[DataService] Testing ETH to Wei conversion:');
    
    const testValues = [
      '0.052511', // Your example value
      '1.0',
      '0.001',
      '100.5',
      '0'
    ];
    
    testValues.forEach(ethValue => {
      const weiValue = this.ethToWei(ethValue);
      const backToEth = formatEther(BigInt(weiValue));
      console.log(`ETH: ${ethValue} -> Wei: ${weiValue} -> Back to ETH: ${backToEth}`);
    });
  }

  getFloor(): number {
    return this.currentFloor.getValue();
  }

  /** Subgraph / Larva / PunksMarket: native listing (no external marketplace `source`). */
  private static isNativeListing(listing: Listing | null | undefined): boolean {
    return (
      !!listing?.value &&
      listing.value !== '0' &&
      (!listing.source || !!listing.nativePunksmarket)
    );
  }

  /** If a token has a native listing, keep only that row; otherwise same merge as before for externals. */
  private mergeMarketGridToken(map: Map<string, Punk>, token: Punk): void {
    const existing = map.get(token.id);

    if (token.listing && DataService.isNativeListing(token.listing)) {
      map.set(token.id, token);
      return;
    }

    if (existing?.listing && DataService.isNativeListing(existing.listing)) {
      return;
    }

    if (!token.listing) {
      map.set(token.id, token);
      return;
    }

    if (!existing?.listing) {
      map.set(token.id, token);
      return;
    }

    try {
      if (BigInt(token.listing.value) < BigInt(existing.listing.value)) {
        map.set(token.id, token);
      }
    } catch {
      if (token.listing.value < existing.listing.value) {
        map.set(token.id, token);
      }
    }
  }

  private buildMergedMarketPunks(
    nftxVaultData: Punk[],
    gondiData: Punk[],
    marketData: Punk[],
    reservoirData: Punk[],
    punksmarketV1Data: Punk[] = [],
  ): Punk[] {
    const datas = [
      ...nftxVaultData,
      ...gondiData,
      ...marketData,
      ...reservoirData,
      ...punksmarketV1Data,
    ];
    const uniqueTokens = new Map<string, Punk>();
    datas.forEach((token) => this.mergeMarketGridToken(uniqueTokens, token));
    return Array.from(uniqueTokens.values());
  }

  /** Base URL for nft-indexer REST (alternativeGraphURI without /graphql). */
  private getSalesIndexerBaseUrl(): string | null {
    let base = environment.alternativeGraphURI?.trim();
    if (!base) {
      return null;
    }
    if (base.endsWith('/graphql')) {
      base = base.slice(0, -'/graphql'.length);
    }
    return base.replace(/\/$/, '');
  }

  /**
   * Active native PunksMarket listings from GET /listings/punksmarket-v1 (nft-indexer).
   */
  getPunksmarketV1Listings(
    sortBy: 'priceAsc' | 'tokenId' = 'priceAsc',
    tokenIds?: string[],
    limit = 10_000,
  ): Observable<Punk[]> {
    if (environment.collection !== 'cryptopunks-v1') {
      return of([]);
    }
    const base = this.getSalesIndexerBaseUrl();
    if (!base) {
      return of([]);
    }

    const params: Record<string, string> = {
      sortBy,
      limit: String(limit),
    };
    if (tokenIds?.length) {
      params.tokenIds = tokenIds.join(',');
    }

    return this.http
      .get<{
        listings: Array<{
          tokenId: string;
          amountEth: number;
          offeredDate: string;
          seller?: string;
          tx?: string;
        }>;
      }>(`${base}/listings/punksmarket-v1`, { params })
      .pipe(
        map((res) =>
          (res.listings ?? []).map((row) => this.mapPunksmarketV1ListingToPunk(row)),
        ),
        switchMap((punks) => this.addAttributes(punks)),
        catchError((error) => {
          console.error('[DataService] getPunksmarketV1Listings failed:', error);
          return of([]);
        }),
      );
  }

  /**
   * Single-token lookup for item detail (GET /listings/punksmarket-v1?tokenIds=…).
   */
  getPunksmarketV1ListingForToken(tokenId: string): Observable<Punk | null> {
    return this.getPunksmarketV1Listings('priceAsc', [tokenId], 1).pipe(
      map((punks) => punks[0] ?? null),
    );
  }

  private mapPunksmarketV1ListingToPunk(row: {
    tokenId: string;
    amountEth: number;
    offeredDate: string;
  }): Punk {
    return {
      id: row.tokenId,
      wrapped: false,
      attributes: [],
      listing: {
        id: row.tokenId,
        value: this.ethToWei(row.amountEth),
        blockTimestamp: row.offeredDate,
        nativePunksmarket: true,
        toAccount: { id: PUNKSMARKET_V1_ROUTER_ADDRESS, punks: [] },
      },
    };
  }

  /** Native PunksMarket V1 listings without attribute enrichment (for owned merge). */
  private getPunksmarketV1ListingsRaw(
    options: { tokenIds?: string[]; seller?: string; limit?: number } = {},
  ): Observable<Punk[]> {
    if (environment.collection !== 'cryptopunks-v1') {
      return of([]);
    }
    const base = this.getSalesIndexerBaseUrl();
    if (!base) {
      return of([]);
    }

    const { tokenIds, seller, limit = 10_000 } = options;
    const params: Record<string, string> = {
      sortBy: 'priceAsc',
      limit: String(limit),
    };
    if (tokenIds?.length) {
      params.tokenIds = tokenIds.join(',');
    }
    if (seller?.trim()) {
      params.seller = seller.trim().toLowerCase();
    }

    return this.http
      .get<{
        listings: Array<{
          tokenId: string;
          amountEth: number;
          offeredDate: string;
          seller?: string;
        }>;
      }>(`${base}/listings/punksmarket-v1`, { params })
      .pipe(
        map((res) =>
          (res.listings ?? [])
            .filter((row) => {
              if (!seller?.trim()) {
                return true;
              }
              return row.seller?.trim().toLowerCase() === seller.trim().toLowerCase();
            })
            .map((row) => this.mapPunksmarketV1ListingToPunk(row)),
        ),
        catchError((error) => {
          console.error('[DataService] getPunksmarketV1ListingsRaw failed:', error);
          return of([]);
        }),
      );
  }

  /** Fetch PunksMarket listings for dashboard owned merge (seller-scoped, batched tokenIds). */
  private getPunksmarketV1ListingsForOwned(
    seller: string,
    tokenIds: string[],
  ): Observable<Punk[]> {
    const normalizedSeller = seller.trim().toLowerCase();
    const chunkSize = 150;

    if (tokenIds.length <= chunkSize) {
      return this.getPunksmarketV1ListingsRaw({
        seller: normalizedSeller,
        tokenIds,
      });
    }

    const chunks: string[][] = [];
    for (let i = 0; i < tokenIds.length; i += chunkSize) {
      chunks.push(tokenIds.slice(i, i + chunkSize));
    }

    return forkJoin(
      chunks.map((chunk) =>
        this.getPunksmarketV1ListingsRaw({
          seller: normalizedSeller,
          tokenIds: chunk,
        }),
      ),
    ).pipe(
      map((results) => {
        const byId = new Map<string, Punk>();
        results.flat().forEach((punk) => byId.set(punk.id, punk));
        return Array.from(byId.values());
      }),
    );
  }

  private fetchListedTokensForOwned(
    collections: string[],
    tokenIds: string[],
  ): Observable<any[]> {
    if (!tokenIds.length) {
      return of([]);
    }

    return this.apollo.use('alternative').query<any>({
      query: GET_LISTED_TOKENS,
      variables: {
        collections,
        tokenIds,
      },
      fetchPolicy: 'network-only',
    }).pipe(
      map((result) => result.data?.getListedTokens || []),
      catchError((error) => {
        console.error('[DataService] fetchListedTokensForOwned failed:', error);
        return of([]);
      }),
    );
  }

  /**
   * OpenSea / Blur listings for owned punks from listing-indexer (same source as the grid).
   * Sales-indexer `getListedTokens` does not expose marketplace platform on prod yet.
   */
  private fetchMarketplaceListingsForOwned(
    owner: string,
    tokenIds: string[],
  ): Observable<Map<string, Listing>> {
    if (!environment.featureFlags?.USE_ALTERNATIVE_LISTINGS_API || !tokenIds.length) {
      return of(new Map());
    }

    const ownedTokenIds = new Set(tokenIds);
    const normalizedOwner = owner.trim().toLowerCase();

    return this.getCustomIndexerListings().pipe(
      map((listings: any[]) => {
        const listingsMap = new Map<string, Listing>();

        listings.forEach((listing) => {
          if (!ownedTokenIds.has(listing.tokenId)) {
            return;
          }
          if (listing.currentOwner?.trim().toLowerCase() !== normalizedOwner) {
            return;
          }

          const mapped = mapMarketplaceIndexerListingToListing(
            listing,
            (amount) => this.ethToWei(amount),
          );
          if (!mapped) {
            return;
          }

          const merged = mergeOwnedListings(listingsMap.get(listing.tokenId), mapped);
          if (merged) {
            listingsMap.set(listing.tokenId, merged);
          }
        });

        return listingsMap;
      }),
      catchError((error) => {
        console.error('[DataService] fetchMarketplaceListingsForOwned failed:', error);
        return of(new Map());
      }),
    );
  }

  /** True when listing is a native V1 offer to the PunksMarket router. */
  static isPunksmarketNativeListing(punk: Punk | null | undefined): boolean {
    if (!punk?.listing?.value || punk.listing.value === '0') {
      return false;
    }
    if (punk.listing.nativePunksmarket) {
      return true;
    }
    return isPunksmarketRouterAddress(punk.listing.toAccount?.id);
  }

  getAttributes(): Observable<any> {
    console.log(`[DataService] getAttributes started`);
    if (this.attributesData) {
      console.log(`[DataService] getAttributes returning cached data`);
      return of(this.attributesData);
    }
    console.log(`[DataService] getAttributes fetching from punkcdn.com`);
    return this.http.get(`https://punkcdn.com/data/_attributes.json`).pipe(
      tap(() => console.log(`[DataService] getAttributes HTTP request started`)),
      timeout(3000), // 3 seconds timeout for SSR
      tap((res) => {
        console.log(`[DataService] getAttributes completed successfully`);
        this.attributesData = res;
      }),
      catchError((error) => {
        console.error('[DataService] Error fetching attributes:', error);
        if (error.name === 'TimeoutError') {
          console.warn('[DataService] getAttributes timeout reached, returning empty object');
        }
        return of({}); // Return empty object in case of error
      })
    );
  }

  addAttributes(punks: Punk[]): Observable<Punk[]> {
    return this.getAttributes().pipe(
      map((res: any) => {
        return punks.map((item: Punk) => ({
          ...item,
          attributes: res[item.id] || []
        }));
      })
    );
  }

  ///////////////////////////////////////////////////////////////////////////////
  // OWNED //////////////////////////////////////////////////////////////////////
  ///////////////////////////////////////////////////////////////////////////////

  fetchOwned(address: string): Observable<Punk[]> {
    if (!address) return of([]);
    /*
    if (!environment.production) {
      return of([
        {
          id: '1',
          wrapped: false,
          attributes: []
        },
        {
          id: '2',
          wrapped: true,
          attributes: []
        },
        {
          id: '3',
          wrapped: false,
          attributes: []
        }, 
        {
          id: '2306',
          wrapped: false,
          attributes: []
        },
      ])
    }
      */

    // Check if we should use the alternative REST endpoint
    const useAlternativeEndpoint = environment.featureFlags?.USE_OUR_SALES_INDEXER_FOR_OWNERSHIP || false;
    
    if (useAlternativeEndpoint && environment.alternativeGraphURI) {
      // Build collections array based on collection type
      const collections: string[] = [environment.wrappedPunksAddress];
      
      if (environment.collection === 'cryptopunks') {
        // Punks v2: add punksMarketAddress and c721Address
        collections.push(environment.punksMarketAddress);
        if (environment.c721Address && environment.c721Address !== '0x0000000000000000000000000000000000000000') {
          collections.push(environment.c721Address);
        }
      } else if (environment.collection === 'cryptopunks-v1') {
        // Punks v1: add punksMarketAddress
        collections.push(environment.punksMarketAddress);
        collections.push(environment.v1PunksMarketAddress!);
      }
      // For phunks (cryptophunks), only wrappedPunksAddress is included

      // Construct base URL - remove /graphql if present
      let baseUrl = environment.alternativeGraphURI;
      if (baseUrl.endsWith('/graphql')) {
        baseUrl = baseUrl.replace(/\/graphql$/, '');
      }
      
      // Construct REST endpoint URL
      const ownedUrl = `${baseUrl}/owned/${address}`;
      const ownedParams = { collections: collections.join(',') };

      return this.http.get(ownedUrl, { params: ownedParams }).pipe(
        map((response: any) => {
          // Response is an array: [{"tokenId":"1580","collection":"0x...","tx":"0x...","txDate":"...","ownedSinceDays":664}]
          if (!environment.production) {
            return [
              {
                id: '1',
                wrapped: false,
                attributes: []
              },
              {
                id: '2',
                wrapped: true,
                attributes: []
              },
              {
                id: '3',
                wrapped: false,
                attributes: []
              }, 
              {
                id: '5170',
                wrapped: false,
                attributes: []
              },
              {
                id: '6596',
                wrapped: false,
                attributes: []
              },
            ]            
          }
          
          if (!Array.isArray(response)) {
            return [];
          }
          
          // Map response to match subgraph format
          return response.map((item: any) => {
            const collection = item.collection?.toLowerCase() || '';
            const wrappedPunksAddr = environment.wrappedPunksAddress?.toLowerCase() || '';
            const c721Addr = environment.c721Address?.toLowerCase() || '';
            
            const wrapped =
              item.wrapped === true || collection === wrappedPunksAddr;
            
            return {
              id: item.tokenId,
              wrapped,
              collection,
              c721wrapped: collection === c721Addr,
              bid: null,
              listing: null,
              attributes: [], // Will be populated by addAttributes()
            };
          });
        }),
        switchMap((punks: Punk[]) => {
          const tokenIds = punks.map((p) => p.id);
          const ownedTokenIds = new Set(tokenIds);

          return forkJoin({
            listed: this.fetchListedTokensForOwned(collections, tokenIds),
            punksmarketV1: this.getPunksmarketV1ListingsForOwned(address, tokenIds),
            marketplace: this.fetchMarketplaceListingsForOwned(address, tokenIds),
          }).pipe(
            map(({ listed, punksmarketV1, marketplace }) => {
              const listingsMap = new Map<string, Listing>();
              const punksmarketMap = new Map<string, Listing>();

              listed.forEach((listedToken: any) => {
                const listing = mapIndexerListedTokenToListing(
                  listedToken,
                  (amount) => this.ethToWei(amount),
                );
                const merged = mergeOwnedListings(
                  listingsMap.get(listedToken.tokenId),
                  listing,
                );
                if (merged) {
                  listingsMap.set(listedToken.tokenId, merged);
                }
              });

              punksmarketV1.forEach((punk) => {
                if (punk.listing && ownedTokenIds.has(punk.id)) {
                  punksmarketMap.set(punk.id, punk.listing);
                }
              });

              return punks.map((punk) => ({
                ...punk,
                listing: mergeOwnedListings(
                  listingsMap.get(punk.id),
                  marketplace.get(punk.id),
                  punksmarketMap.get(punk.id),
                ),
              }));
            }),
          );
        }),
        switchMap((punksWithListings: Punk[]) => {
          // Extract tokenIds from punks for filtering
          const tokenIds = punksWithListings.map(p => p.id);
          const tokenIdsSet = new Set(tokenIds);
          
          // Fetch all active bids from the old graph (subgraph)
          return this.apollo.query<any>({
            query: GET_BIDS_FOR_TOKENS,
            fetchPolicy: 'network-only'
          }).pipe(
            map((result: any) => {
              const allBids = result.data?.bids || [];
              
              // Filter bids to only those matching owned punks
              const relevantBids = allBids.filter((bid: any) => {
                const punkId = bid.punk?.id;
                return punkId && tokenIdsSet.has(punkId);
              });
              
              // Create a map of tokenId -> bid for quick lookup
              // Keep only the most recent bid per punk (highest blockTimestamp)
              const bidsMap = new Map<string, any>();
              relevantBids.forEach((bid: any) => {
                const punkId = bid.punk.id;
                const existingBid = bidsMap.get(punkId);
                if (!existingBid || (bid.blockTimestamp && existingBid.blockTimestamp && parseInt(bid.blockTimestamp) > parseInt(existingBid.blockTimestamp))) {
                  bidsMap.set(punkId, {
                    id: bid.id,
                    value: bid.value,
                    blockTimestamp: bid.blockTimestamp,
                    fromAccount: bid.fromAccount,
                    transactionHash: bid.transactionHash
                  });
                }
              });
              
              // Merge bids with punks
              return punksWithListings.map(punk => ({
                ...punk,
                bid: bidsMap.get(punk.id) || null
              }));
            }),
            catchError((error) => {
              console.log('Error fetching bids for tokens:', error);
              // Return punks without bids if error occurs
              return of(punksWithListings);
            })
          );
        }),
        switchMap((res: Punk[]) => this.addAttributes(res)),
        tap((res: any) => {
          if (environment.collection === 'cryptopunks-v1') {
            res.sort((a: any, b: any) => {
              // Place wrapped first
              if (a.wrapped === b.wrapped) {
                // Then by id as number
                return Number(a.id) - Number(b.id);
              }
              return a.wrapped ? -1 : 1;
            });
          }
        }),
        catchError((error) => {
          console.log('Error fetching ownership data from alternative endpoint:', error);
          return of([]);
        }),
      );
    }
    
    const pageLimit = 1000; 
    let skip = 0;
    const fetchPage = (skip: number, acc: Punk[] = []): Observable<any> => {
      const q: QueryOptions = {
        query: this.graphQLQueriesService.getQuery(environment.collection, 'GET_OWNED'),
        variables: { address, limit: pageLimit, skip },
      };

      return this.apollo.query(q).pipe(
        map((res: any) => res.data?.account || {}),
        switchMap((account: any) => {
          const punks = account.punks || [];
          const allPunks = [...acc, ...punks];
          if (punks.length === pageLimit) {
            return fetchPage(skip + pageLimit, allPunks).pipe(
              map((nextAccount: any) => ({
                ...account,
                punks: nextAccount.punks,
              }))
            );
          } else {
            return of({ ...account, punks: allPunks });
          }
        })
      );
    };

    const wrappedUrl = `https://api.reservoir.tools/users/${address}/tokens/v7`;
    const wrappedParams = { contract: environment.wrappedPunksAddress };
    const wrappedCall$ = this.http.get(wrappedUrl, { params: wrappedParams }).pipe(
      map((res: any) => res.tokens?.length ? res.tokens : []),
      map((res: any) => {
        if (!res.length) return [];
        return res.map((item: any) => {
          return {
            id: item.token.tokenId,
            wrapped: true,
            listing: item.ownership.floorAsk.price ? {
              id: item.token?.tokenId,
              value: item.ownership.floorAsk.price.amount.raw,
              usd: Math.round(item.ownership.floorAsk.price.amount.usd).toString(),
              blockTimestamp: item.ownership.floorAsk.validFrom,
              source: item.ownership.floorAsk.source
            } : null,
          };
        });
      }),
    );

    return fetchPage(0).pipe(
      map((res: any) => res.punks || []),
      /*
      switchMap((res: any) => {
        if (environment.collection !== 'cryptopunks') return of(res)
        return wrappedCall$.pipe(
          map((wrapped) => [ ...res, ...wrapped ]),
        );
      }),
      */
      switchMap((res: any) => this.addAttributes(res)),
      tap((res: any) => {
        if (environment.collection === 'cryptopunks-v1') {
          res
            .sort((a: any, b: any) => {
              // Place wrapped first
              if (a.wrapped === b.wrapped) {
                // Then by id as number
                return Number(a.id) - Number(b.id);
              }
              return a.wrapped ? -1 : 1;
            })
        }        
      }),
      catchError((error) => {
        console.log('Error fetching data:', error);
        return of([]);
      }),
    );
  }

  /**
   * Lightweight fetch: returns raw owned-token entries (id + wrappingStatus)
   * without the heavy bids/listings/attributes pipeline.
   */
  fetchOwnedIds(address: string, collections: string[]): Observable<{ tokenId: string; wrappingStatus?: string }[]> {
    if (!address) return of([]);

    let baseUrl = environment.alternativeGraphURI ?? '';
    if (baseUrl.endsWith('/graphql')) {
      baseUrl = baseUrl.replace(/\/graphql$/, '');
    }

    const url = `${baseUrl}/owned/${address}`;
    const params = { collections: collections.join(',') };

    return this.http.get<any[]>(url, { params }).pipe(
      map(response => Array.isArray(response) ? response.map((item: any) => ({
        tokenId: item.tokenId as string,
        wrappingStatus: item.wrappingStatus as string | undefined,
      })) : []),
      catchError(() => of([])),
    );
  }

  fetchTokenOwner(tokenId: string, collection: string): Observable<string | null> {
    let baseUrl = environment.alternativeGraphURI ?? '';
    if (baseUrl.endsWith('/graphql')) {
      baseUrl = baseUrl.replace(/\/graphql$/, '');
    }

    return this.http
      .post<{ owners: { tokenId: string; owner: string }[] }>(`${baseUrl}/token-owners/bulk`, {
        collection,
        tokenIds: [tokenId],
      })
      .pipe(
        map((res) => res.owners?.[0]?.owner ?? null),
        catchError(() => of(null)),
      );
  }

  /** Collection for token_ownership lookup — same rule of thumb as /owned. */
  private salesIndexerOwnershipCollection(punk: Punk): string | null {
    if (environment.collection === 'cryptopunks-v1') {
      return punk.wrapped
        ? environment.wrappedPunksAddress ?? null
        : environment.v1PunksMarketAddress ?? null;
    }
    if (environment.collection === 'cryptopunks') {
      if (punk.c721wrapped && environment.c721Address !== ZERO_ADDRESS) {
        return environment.c721Address;
      }
      return punk.wrapped ? environment.wrappedPunksAddress : environment.punksMarketAddress;
    }
    return environment.wrappedPunksAddress ?? null;
  }

  /** When USE_OUR_SALES_INDEXER_FOR_OWNERSHIP, replace subgraph owner on detail pages. */
  private applySalesIndexerOwnership(punk: Punk): Observable<Punk> {
    if (
      !environment.featureFlags?.USE_OUR_SALES_INDEXER_FOR_OWNERSHIP ||
      !environment.alternativeGraphURI ||
      !punk?.id
    ) {
      return of(punk);
    }

    const collection = this.salesIndexerOwnershipCollection(punk);
    if (!collection) {
      return of(punk);
    }

    return this.fetchTokenOwner(punk.id, collection).pipe(
      map((owner) => {
        if (!owner) {
          return punk;
        }
        const id = owner.toLowerCase();
        return {
          ...punk,
          ...(punk.wrapped || punk.c721wrapped ? { wrappedOwner: id } : {}),
          owner: { id, punks: punk.owner?.punks ?? [] },
        };
      }),
    );
  }

  getOwned(): Punk[] {
    return this.ownedData.getValue();
  }

  setOwned(owned: Punk[]): void {
    console.log('owned', owned)
    this.ownedData.next(owned);

  }

  getAllData(): Observable<Punk[]> {
    console.log(`[DataService] getAllData started - isServer: ${this.platformService.isServer}`);
    console.log(`[DataService] getAllData - marketData$ current value:`, this.marketData.getValue().length, 'items');
    
    return this.marketData$.pipe(
      tap((data) => {
        console.log(`[DataService] getAllData - marketData$ emitted:`, data?.length || 0, 'items, isServer:', this.platformService.isServer);
      }),
      switchMap(() => {
        console.log(`[DataService] getAllData - calling getAttributes()`);
        return this.getAttributes();
      }),
      map((attributes) => {
        console.log(`[DataService] getAllData - attributes received, processing data`);
        const all = Object.keys(attributes).map((k) => ({
          id: k,
          attributes: (attributes as any)[k],
        }));
        for (const punk of this.marketData.getValue()) {
          const i = Number(punk.id);
          all[i] = punk;
        }
        console.log(`[DataService] getAllData - returning ${all.length} items`);
        return all;
      })
    );
  }

  ///////////////////////////////////////////////////////////////////////////////
  // MARKET DATA ////////////////////////////////////////////////////////////////
  ///////////////////////////////////////////////////////////////////////////////

  getMarketData(): void {
    console.log(`[DataService] getMarketData started - isServer: ${this.platformService.isServer}`);
    
    let bidSkip = 0;
    let listingSkip = 0;
    let isFirst = true;

    // Helper function to filter bids based on filteredTokenFromBids
    const filterBidsByTokenIds = (bids: any[]) => {
      if (!environment.filteredTokenFromBids || environment.filteredTokenFromBids.length === 0) {
        return bids;
      }
      return bids.filter((bid: any) => !environment.filteredTokenFromBids.includes(bid.punk?.id));
    };

    const fetchDataWithCursorServer = (bidSkip: number, listingSkip: number) => {
      console.log(`[DataService] [SSR] fetchDataWithCursorServer started - bidSkip: ${bidSkip}, listingSkip: ${listingSkip}`);
      return this.apollo.query({
        query: this.graphQLQueriesService.getQuery(environment.collection, 'GET_ALL_LISTINGS_BIDS'),
        variables: { 
          bidSkip: bidSkip, 
          listingSkip: listingSkip, 
          limit: 1000,
          filteredTokenIds: environment.filteredTokenFromBids && environment.filteredTokenFromBids.length > 0 ? environment.filteredTokenFromBids : []
        },
      }).pipe(
        timeout(10000),
        tap(() => console.log(`[DataService] [SSR] GET_ALL_LISTINGS_BIDS GraphQL query completed`)),
        catchError((error) => {
          console.error('[DataService] [SSR] Error in GET_ALL_LISTINGS_BIDS query:', error);
          if (error.name === 'TimeoutError') {
            console.warn('[DataService] [SSR] GET_ALL_LISTINGS_BIDS timeout reached, returning empty data');
          }
          return of({ data: { bids: [], listings: [] } });
        }),
        map((result) => ({
          bids: filterBidsByTokenIds((result.data as any).bids),
          listings: (result.data as any).listings,
        }))
      );
    };

    const fetchDataWithCursorClient = (bidSkip: number, listingSkip: number) => {
      console.log(`[DataService] [Client] fetchDataWithCursorClient started - bidSkip: ${bidSkip}, listingSkip: ${listingSkip}`);
      return this.apollo.query({
        query: this.graphQLQueriesService.getQuery(environment.collection, 'GET_ALL_LISTINGS_BIDS'),
        variables: { 
          bidSkip: bidSkip, 
          listingSkip: listingSkip, 
          limit: 1000,
          filteredTokenIds: environment.filteredTokenFromBids && environment.filteredTokenFromBids.length > 0 ? environment.filteredTokenFromBids : []
        },
      }).pipe(
        timeout(10000),
        tap(() => console.log(`[DataService] [Client] GET_ALL_LISTINGS_BIDS GraphQL query completed`)),
        catchError((error) => {
          console.error('[DataService] [Client] Error in GET_ALL_LISTINGS_BIDS query:', error);
          if (error.name === 'TimeoutError') {
            console.warn('[DataService] [Client] GET_ALL_LISTINGS_BIDS timeout reached, returning empty data');
          }
          return of({ data: { bids: [], listings: [] } });
        }),
        map((result) => ({
          bids: filterBidsByTokenIds((result.data as any).bids),
          listings: (result.data as any).listings,
        }))
      );
    };

    // WatchQuery for first page with real-time updates (client only)
    const watchFirstPage = () => {
      console.log('[DataService] [Client] Starting watchQuery for first page with polling');
      return this.apollo.watchQuery({
        query: this.graphQLQueriesService.getQuery(environment.collection, 'GET_ALL_LISTINGS_BIDS'),
        variables: { 
          bidSkip: 0, 
          listingSkip: 0, 
          limit: 1000,
          filteredTokenIds: environment.filteredTokenFromBids && environment.filteredTokenFromBids.length > 0 ? environment.filteredTokenFromBids : []
        },
        fetchPolicy: 'cache-and-network',
        errorPolicy: 'all',
        notifyOnNetworkStatusChange: true,
        pollInterval: pollInterval, // Poll every 30 seconds
      }).valueChanges.pipe(
        tap(() => console.log(`[DataService] [Client] watchQuery first page completed`)),
        map((result) => ({
          bids: filterBidsByTokenIds((result.data as any).bids),
          listings: (result.data as any).listings,
        }))
      );
    };

    const recursiveFetch = (
      bidSkip: number, 
      listingSkip: number, 
      bids: any[] = [], 
      listings: any[] = [],
      depth: number = 0
    ): Observable<any> => {
      // Safety limit to prevent infinite recursion
      if (depth > 10) {
        console.warn('[DataService] Recursive fetch depth limit reached, stopping recursion');
        return of({ bids, listings });
      }
      
      const fetcher = this.platformService.isServer ? fetchDataWithCursorServer : fetchDataWithCursorClient;
      console.log(`[DataService] recursiveFetch depth ${depth}, bidSkip: ${bidSkip}, listingSkip: ${listingSkip}`);
      return fetcher(bidSkip, listingSkip).pipe(
        tap(() => console.log(`[DataService] recursiveFetch depth ${depth} - fetcher completed`)),
        switchMap(({ bids: newBids, listings: newListings }) => {
          console.log(`[DataService] recursiveFetch depth ${depth} - got ${newBids.length} bids, ${newListings.length} listings`);
          const allBids = [...bids, ...newBids];
          const allListings = [...listings, ...newListings];

          if (newBids.length === 1000 || newListings.length === 1000) {
            console.log(`[DataService] recursiveFetch depth ${depth} - continuing recursion`);
            return recursiveFetch(
              bidSkip + newBids.length,
              listingSkip + newListings.length,
              allBids,
              allListings,
              depth + 1
            );
          }

          console.log(`[DataService] recursiveFetch depth ${depth} - ending recursion, total: ${allBids.length} bids, ${allListings.length} listings`);
          return of({ bids: allBids, listings: allListings });
        })
      );
    };

    if (this.platformService.isServer) {
      // SSR: fetch unique with recursive logic
      console.log('[DataService] [SSR] Starting recursiveFetch');
      recursiveFetch(bidSkip, listingSkip).pipe(
        map(({ bids, listings }) => {
          const merged: any = {};
          for (const listing of listings) merged[listing.id] = {
            ...merged[listing.id],
            id: listing.id,
            listing,
          };
          for (const bid of bids) merged[bid.id] = {
            ...merged[bid.id],
            id: bid.id,
            bid,
          };
          return Object.values(merged) as Punk[];
        }),
        switchMap((res: any) => this.addAttributes(res)),
        switchMap((res: any) => {
          console.log(`[DataService] [SSR] Starting combineLatest for additional data sources`);
          return combineLatest([
            this.fetchNftxVault(this.stateSvc.activeMarketplaceFilterModel === 'NFTX' ? 1000 : 1),
            this.fetchGondiListings(100),
            this.getReservoirData(),
            this.getPunksmarketV1Listings(),
            of(res)
          ]).pipe(
            timeout(15000),
            tap(() => console.log(`[DataService] [SSR] combineLatest completed`)),
            catchError((error) => {
              console.error('[DataService] [SSR] Error in combineLatest:', error);
              if (error.name === 'TimeoutError') {
                console.warn('[DataService] [SSR] combineLatest timeout reached, returning empty data');
              }
              return of([[], [], [], [], res]);
            })
          );
        }),
        tap(([ nftxVaultData, gondiData, reservoirData, punksmarketV1Data, marketData ]) => {
          console.log('[DataService] [SSR] combineLatest result:', {
            nftxVaultDataLength: nftxVaultData?.length || 0,
            gondiDataLength: gondiData?.length || 0,
            reservoirDataLength: reservoirData?.length || 0,
            punksmarketV1Length: punksmarketV1Data?.length || 0,
            marketDataLength: marketData?.length || 0
          });
          const finalDatas = this.buildMergedMarketPunks(
            nftxVaultData,
            gondiData,
            marketData,
            reservoirData,
            punksmarketV1Data,
          );
          console.log(`[DataService] [SSR] getMarketData - about to next marketData: ${finalDatas.length} items`);
          this.marketData.next(finalDatas);
          console.log(`[DataService] [SSR] getMarketData - marketData.next() called with ${finalDatas.length} items`);
          const anySource =
            (nftxVaultData?.length || 0) +
              (gondiData?.length || 0) +
              (marketData?.length || 0) +
              (reservoirData?.length || 0) +
              (punksmarketV1Data?.length || 0) >
            0;
          if (anySource) {
            this.stateSvc.setMarketLoading(false);
          }

          // Update aggregated floor (all marketplaces)
          const floorWeiList = finalDatas
            .map((t: Punk) => t.listing?.value)
            .filter((v: any) => {
              // Filter out invalid values: null, undefined, '0', empty strings, Infinity, NaN
              if (!v || v === '0' || v === '') return false;
              try {
                // Try to convert to BigInt to validate the value
                BigInt(v);
                return true;
              } catch {
                return false;
              }
            }) as string[];
          if (floorWeiList.length) {
            const minWei = floorWeiList.reduce(
              (min: string, v: string) => {
                try {
                  return BigInt(v) < BigInt(min) ? v : min;
                } catch {
                  // If conversion fails, keep the current min
                  return min;
                }
              },
              floorWeiList[0]
            );
            this.stateSvc.setAggregatedFloor(this.weiToEthPipe.transform(minWei));
          }
        }),
        tap(() => {
          console.log('[DataService] [SSR] getMarketData subscription completed');
        }),
        catchError((error) => {
          console.error('[DataService] [SSR] Error in getMarketData:', error);
          this.stateSvc.setMarketLoading(false);
          return of(null);
        })
      ).subscribe();
    } else {
      // Client: WatchQuery for first page + recursive fetch for remaining pages
      console.log('[DataService] [Client] Starting watchQuery for first page + recursive fetch');
      
      // First, get all historical data with recursive fetch
      recursiveFetch(1000, 1000).pipe(
        tap(() => {
          bidSkip = 0;
          listingSkip = 0;
        }),
        map(({ bids: historicalBids, listings: historicalListings }) => {
          console.log(`[DataService] [Client] Got historical data: ${historicalBids.length} bids, ${historicalListings.length} listings`);
          
          // Now watch the first page for real-time updates
          return watchFirstPage().pipe(
            map(({ bids: firstPageBids, listings: firstPageListings }) => {
              console.log(`[DataService] [Client] Got first page data: ${firstPageBids.length} bids, ${firstPageListings.length} listings`);
              
              // Combine first page (real-time) with historical data
              const allBids = [...firstPageBids, ...historicalBids];
              const allListings = [...firstPageListings, ...historicalListings];
              
              const merged: any = {};
              for (const listing of allListings) merged[listing.id] = {
                ...merged[listing.id],
                id: listing.id,
                listing,
              };
              for (const bid of allBids) merged[bid.id] = {
                ...merged[bid.id],
                id: bid.id,
                bid,
              };
              return Object.values(merged) as Punk[];
            })
          );
        }),
        switchMap((firstPageObservable) => firstPageObservable),
        switchMap((res: any) => this.addAttributes(res)),
        switchMap((res: any) => {
          console.log(`[DataService] [Client] Starting combineLatest for additional data sources`);
          return combineLatest([
            this.fetchNftxVault(this.stateSvc.activeMarketplaceFilterModel === 'NFTX' ? 1000 : 1),
            this.fetchGondiListings(100),
            this.getReservoirData(),
            this.getPunksmarketV1Listings(),
            of(res)
          ]).pipe(
            timeout(15000),
            tap(() => console.log(`[DataService] [Client] combineLatest completed`)),
            catchError((error) => {
              console.error('[DataService] [Client] Error in combineLatest:', error);
              if (error.name === 'TimeoutError') {
                console.warn('[DataService] [Client] combineLatest timeout reached, returning empty data');
              }
              return of([[], [], [], [], res]);
            })
          );
        }),
        tap(([ nftxVaultData, gondiData, reservoirData, punksmarketV1Data, marketData ]) => {
          console.log('[DataService] [Client] combineLatest result:', {
            nftxVaultDataLength: nftxVaultData?.length || 0,
            gondiDataLength: gondiData?.length || 0,
            reservoirDataLength: reservoirData?.length || 0,
            punksmarketV1Length: punksmarketV1Data?.length || 0,
            marketDataLength: marketData?.length || 0
          });
          const finalDatas = this.buildMergedMarketPunks(
            nftxVaultData,
            gondiData,
            marketData,
            reservoirData,
            punksmarketV1Data,
          );
          console.log(`[DataService] [Client] getMarketData - about to next marketData: ${finalDatas.length} items`);
          this.marketData.next(finalDatas);
          console.log(`[DataService] [Client] getMarketData - marketData.next() called with ${finalDatas.length} items`);
          const anySource =
            (nftxVaultData?.length || 0) +
              (gondiData?.length || 0) +
              (marketData?.length || 0) +
              (reservoirData?.length || 0) +
              (punksmarketV1Data?.length || 0) >
            0;
          if (anySource) {
            this.stateSvc.setMarketLoading(false);
          }

          // Update aggregated floor (all marketplaces)
          const floorWeiList = finalDatas
            .map((t: Punk) => t.listing?.value)
            .filter((v: any) => {
              // Filter out invalid values: null, undefined, '0', empty strings, Infinity, NaN
              if (!v || v === '0' || v === '') return false;
              try {
                // Try to convert to BigInt to validate the value
                BigInt(v);
                return true;
              } catch {
                return false;
              }
            }) as string[];
          if (floorWeiList.length) {
            const minWei = floorWeiList.reduce(
              (min: string, v: string) => {
                try {
                  return BigInt(v) < BigInt(min) ? v : min;
                } catch {
                  // If conversion fails, keep the current min
                  return min;
                }
              },
              floorWeiList[0]
            );
            this.stateSvc.setAggregatedFloor(this.weiToEthPipe.transform(minWei));
          }
        }),
        catchError((error) => {
          console.error('[DataService] [Client] Error in getMarketData:', error);
          this.stateSvc.setMarketLoading(false);
          return of(null);
        })
      ).subscribe();
    }
  }


  getReservoirBidsForToken(tokenId: string): Observable<any> {
    console.log(`[DataService] getReservoirBidsForToken started for tokenId: ${tokenId}`);
    const params = {
      contracts: [environment.wrappedPunksAddress],
      token: `${environment.wrappedPunksAddress}:${tokenId}`,
      sortBy: 'price',
      sortDirection: 'desc',
      limit: 1000
    };

    return this.http.get(`https://api.reservoir.tools/orders/bids/v5`, {
      params: {
        token: `${environment.wrappedPunksAddress}:${tokenId}`,
        sortBy: 'price',
        sortDirection: 'desc',
        limit: 500
      }
    }).pipe(
      timeout(15000), // 15 secondes timeout
      tap(() => console.log(`[DataService] Reservoir bids API call started for tokenId: ${tokenId}`)),
      map((res: any) => res.orders.filter((bid: any) => environment.collection === 'cryptophunks' ? bid.price.amount.native > 0.03 : bid.price.amount.usd > 2)),
      map((res: any) => res.filter((bid: any) => bid.source.domain !== 'wondoria.io')),
      catchError((error) => {
        console.error('Error fetching reservoir bids for token:', error);
        return of([]);
      })
    );
  }

  /**
   * Get listings data from custom GraphQL indexer API
   */
  getCustomIndexerListings(): Observable<any> {
    console.log(`[DataService] getCustomIndexerListings started`);

    // Map collection to the correct slug for the GraphQL API
    const getCollectionSlug = (collection: string): string => {
      switch (collection) {
        case 'cryptophunks':
          return 'crypto-phunks';
        case 'cryptopunks':
          return 'wrapped-cryptopunks';
        case 'cryptopunks-v1':
          return 'official-v1-punks';
        default:
          console.warn(`[DataService] Unknown collection: ${collection}, using crypto-phunks as fallback`);
          return 'crypto-phunks';
      }
    };

    const collectionSlug = getCollectionSlug(environment.collection);
    console.log(`[DataService] Using collection slug: ${collectionSlug} for collection: ${environment.collection}`);

    const query = gql`
      query GetListingsByCollection($collectionSlug: String!) {
        listingsByCollection(
          collectionSlug: $collectionSlug
          sort: {
            field: PRICE_AMOUNT
            order: ASC
          }
        ) {
          id
          tokenId
          priceAmount
          currentOwner
          platform
        }
      }
    `;

    const graphUrl = environment.alternativeListingsGraphURI || 'http://localhost:3000/graphql';
    console.log(`[DataService] Using GraphQL URL: ${graphUrl}`);

    return this.http.post(graphUrl, {
      query: query.loc?.source?.body || query,
      variables: {
        collectionSlug: collectionSlug
      }
    }, {
      headers: {
        'Content-Type': 'application/json'
      }
    }).pipe(
      tap(() => console.log(`[DataService] Custom indexer GraphQL API call started`)),
      timeout(5000), // 5 seconds timeout for SSR
      map((res: any) => {
        console.log(`[DataService] Custom indexer GraphQL API completed, listings count:`, res.data?.listingsByCollection?.length || 0);
        return res.data?.listingsByCollection || [];
      }),
      catchError((error) => {
        console.error('[DataService] Error fetching custom indexer listings:', error);
        if (error.name === 'TimeoutError') {
          console.warn('[DataService] Custom indexer listings timeout reached, returning empty array');
        }
        console.warn('[DataService] Custom indexer API error, falling back to empty array');
        return of([]);
      })
    );
  }

  getReservoirData(): Observable<any> {
    console.log(`[DataService] getReservoirData started`);

    // Check if we should use custom indexer instead of Reservoir
    if (environment.featureFlags?.USE_ALTERNATIVE_LISTINGS_API) {
      console.log(`[DataService] Using custom indexer API for listings`);
      return this.getCustomIndexerListings().pipe(
        map((listings: any[]) => {
          // Transform custom indexer data to match Reservoir format
          return listings.map((listing: any) => {
            // Get contract address based on collection
            const contractAddress = environment.wrappedPunksAddress;
            const tokenId = listing.tokenId;
            
            // Map platform to source properties
            const getSourceProperties = (platform: string) => {
              switch (platform) {
                case 'Blur':
                  return {
                    domain: 'blur.io',
                    name: 'Blur',
                    icon: 'https://blur.io/favicons/180.png',
                    url: `https://blur.io/asset/${contractAddress}/${tokenId}`
                  };
                case 'OpenSea':
                  return {
                    domain: 'opensea.io',
                    name: 'OpenSea',
                    icon: '/images/opensea-logo.svg',
                    url: `https://opensea.io/assets/ethereum/${contractAddress}/${tokenId}`
                  };
                default:
                  return {
                    domain: 'custom-indexer',
                    name: platform || 'custom-indexer',
                    icon: '',
                    url: ''
                  };
              }
            };

            const sourceProps = getSourceProperties(listing.platform);
            
            return {
              id: listing.tokenId,
              wrapped: true,
              listing: {
                value: this.ethToWei(listing.priceAmount || '0'), // Convert ETH to Wei for compatibility
                blockTimestamp: new Date().toISOString(),
                source: sourceProps
              },
              owner: {id:listing.currentOwner}
            } as Punk;
          });
        }),
        switchMap((res: any) => this.addAttributes(res)),
        catchError((error) => {
          console.error('[DataService] Error processing custom indexer data:', error);
          return of([]);
        })
      );
    }

    const params = {
      contracts: [environment.wrappedPunksAddress],
      sortBy: 'price',
      sortDirection: 'desc',
      limit: 1000,
      excludeSources: ['nftx.io'],
    };

    const listingsCall = this.http.get(`https://api.reservoir.tools/orders/asks/v5`, {params}).pipe(
      tap(() => console.log(`[DataService] Reservoir listings API call started`)),
      timeout(5000), // 5 seconds timeout for SSR
      
      map((res: any) => {
        console.log(`[DataService] Reservoir listings API completed, orders count:`, res.orders?.length || 0);
        
        // Filter tokens if environment is punk v1 and specific criteria are met
        if (environment.collection === 'cryptopunks-v1') {
          const filteredOrders = res.orders?.filter((order: any) => {
            const tokenId = order.criteria?.data?.token?.tokenId;
            const updatedAt = order.updatedAt;
            
            // Filter out token 2787 with specific updatedAt timestamp
            if (tokenId === '2787' && updatedAt === '2025-08-22T09:46:30.153Z') {
              console.log(`[DataService] Filtering out token ${tokenId} with updatedAt ${updatedAt}`);
              return false;
            }
            return true;
          }) || [];
          
          console.log(`[DataService] Filtered orders count:`, filteredOrders.length);
          return filteredOrders;
        }
        
        return res.orders;
      }),
      catchError((error) => {
        console.error('[DataService] Error fetching listingsCall:', error);
        if (error.name === 'TimeoutError') {
          console.warn('[DataService] Reservoir listings timeout reached, returning empty array');
        }
        return of([]);
      })
    );
    /*
    const bidsCall = this.http.get(`https://api.reservoir.tools/orders/bids/v5`, {params}).pipe(
      tap(() => console.log(`[DataService] Reservoir bids API call started`)),
      timeout(5000), // 5 seconds timeout for SSR
      map((res: any) => {
        console.log(`[DataService] Reservoir bids API completed, orders count:`, res.orders?.length || 0);
        return res.orders;
      }),
      catchError((error) => {
        console.error('Error fetching bidsCall:', error);
        if (error.name === 'TimeoutError') {
          console.warn('[DataService] Reservoir bids timeout reached, returning empty array');
        }
        return of([]);
      })
    );
    */
    
    
    function removeDuplicates(listings: any[]) {
      let map = new Map();
  
      for(let listing of listings) {
        let tokenId = listing.criteria.data.token.tokenId;
        let price = listing.price.amount.usd;
        // If the map already has the token ID and the stored price is lower, skip this listing
        if (map.has(tokenId) && map.get(tokenId).price.amount.usd <= price) continue;
        // Otherwise, store/update the map with this listing
        map.set(tokenId, listing);
      }
  
      // Convert the map values to an array (since map.values() returns an iterator)
      return Array.from(map.values());
    }

    return listingsCall.pipe(
      map((res) => removeDuplicates(res)),
      map((res: any) => {
        return res.map((item: any) => ({
          id: item.criteria.data.token?.tokenId,
          wrapped: true,
          listing: {
            value: item.price.amount.raw,
            blockTimestamp: item.validFrom,
            source: item.source
          }
        })) as Punk[];
      }),
      switchMap((res: any) => this.addAttributes(res)),
      catchError((error) => {
        // console.log('Error fetching data:', error);
        return of([]);
      }),
    );
  }

  fetchUserListings(address: string): Observable<any> {
    const formattedAddress = address.toLowerCase();
    const queryLimit = 1000;
  
    // Function to fetch listings based on a specific query
    const fetchListings = (query: any) => {
      return this.apollo.query({
        query: query,
        variables: {
          limit: queryLimit,
          address: formattedAddress,
        },
      }).pipe(
        timeout(10000), // 10 seconds timeout
        map((result: any) => result.data.listings as any[]),
        catchError((error) => {
          console.error('[DataService] Error in fetchUserListings:', error);
          if (error.name === 'TimeoutError') {
            console.warn('[DataService] fetchUserListings timeout reached, returning empty array');
          }
          return of([]);
        })
      );
    };
    
    return fetchListings(GET_USER_LISTINGS);
  }

  fetchUserBids(address: string): Observable<any> {
    const formattedAddress = address.toLowerCase();
    const queryLimit = 1000;
  
    // Function to fetch bids based on a specific query
    const fetchBids = (query: any) => {
      return this.apollo.query({
        query: query,
        variables: {
          limit: queryLimit,
          address: formattedAddress,
        },
      }).pipe(
        timeout(10000), // 10 seconds timeout
        map((result: any) => result.data.bids as any[]),
        catchError((error) => {
          console.error('[DataService] Error in fetchUserBids:', error);
          if (error.name === 'TimeoutError') {
            console.warn('[DataService] fetchUserBids timeout reached, returning empty array');
          }
          return of([]);
        })
      );
    };
  
    return fetchBids(GET_USER_BIDS);
  }

  fetchListingsOfferedTo(address: string): Observable<any> {
    console.log(`[DataService] fetchListingsOfferedTo started for address: ${address}`);
    const formattedAddress = address.toLowerCase();
    const queryLimit = 1000;

    const query = gql`
      query GetListingsOfferedTo($toAccount: String!, $limit: Int!) {
        listings(
          first: $limit,
          where: { toAccount: $toAccount },
          orderBy: "blockTimestamp",
          orderDirection: desc
        ) {
          id
          value
          blockTimestamp
          fromAccount {
            id
          }
          toAccount {
            id
          }
          punk {
            id
          }
        }
      }
    `;

    return this.apollo.query({
      query: query,
      variables: {
        toAccount: formattedAddress,
        limit: queryLimit,
      },
    }).pipe(
      timeout(10000), // 10 seconds timeout
      tap(() => console.log(`[DataService] fetchListingsOfferedTo query completed for address: ${address}`)),
      map((result: any) => {
        console.log(`[DataService] fetchListingsOfferedTo result:`, result.data?.listings?.length || 0, 'listings');
        return result.data?.listings || [];
      }),
      catchError((error) => {
        console.error('[DataService] Error in fetchListingsOfferedTo:', error);
        if (error.name === 'TimeoutError') {
          console.warn('[DataService] fetchListingsOfferedTo timeout reached, returning empty array');
        }
        return of([]);
      })
    );
  }

  ///////////////////////////////////////////////////////////////////////////////
  // STATES /////////////////////////////////////////////////////////////////////
  ///////////////////////////////////////////////////////////////////////////////

  getStatesStats(): Observable<{[key in PeriodKey]: StateStats}> {
    return this.statesData$.pipe(
      map((states) => {

        const periodData: {[key in PeriodKey]: StateStats} = {
          '24h': { sales: 0, volume: 0, average: 0 },
          '7d': { sales: 0, volume: 0, average: 0 },
          '30d': { sales: 0, volume: 0, average: 0 },
          '6m': { sales: 0, volume: 0, average: 0 },
          '1y': { sales: 0, volume: 0, average: 0 },
          'all': { sales: 0, volume: 0, average: 0 }
        };
        
        // Reset the aggregation data
        Object.keys(periodData).forEach((key) => {
          periodData[key as PeriodKey].sales = 0;
          periodData[key as PeriodKey].volume = 0;
          periodData[key as PeriodKey].average = 0;
        });
    
        const now = new Date().getTime();
    
        for (const state of states) {
          const timestamp = new Date(Number(state.timestamp) * 1000).getTime();
          const diffDays = (now - timestamp) / (1000 * 60 * 60 * 24);
          
          // Convert volume from Wei to ETH
          const volumeInEth = Number(state.volume) / 1e18;
          const salesCount = Number(state.sales || 0);
    
          // Update all relevant period buckets
          if (diffDays < 1) {
            periodData['24h'].sales += salesCount;
            periodData['24h'].volume += volumeInEth;
            periodData['24h'].average = periodData['24h'].volume / periodData['24h'].sales;
          }
          if (diffDays < 7) {
            periodData['7d'].sales += salesCount;
            periodData['7d'].volume += volumeInEth;
            periodData['7d'].average = periodData['7d'].volume / periodData['7d'].sales;
          }
          if (diffDays < 30) {
            periodData['30d'].sales += salesCount;
            periodData['30d'].volume += volumeInEth;
            periodData['30d'].average = periodData['30d'].volume / periodData['30d'].sales;
          }
          if (diffDays < 180) {
            periodData['6m'].sales += salesCount;
            periodData['6m'].volume += volumeInEth;
            periodData['6m'].average = periodData['6m'].volume / periodData['6m'].sales;
          }
          if (diffDays < 365) {
            periodData['1y'].sales += salesCount;
            periodData['1y'].volume += volumeInEth;
            periodData['1y'].average = periodData['1y'].volume / periodData['1y'].sales;
          }
          
          // All time stats
          periodData['all'].sales += salesCount;
          periodData['all'].volume += volumeInEth;
          periodData['all'].average = periodData['all'].volume / periodData['all'].sales;
        }
    
        // Round volume numbers to 2 decimal places
        Object.keys(periodData).forEach((key) => {
          periodData[key as PeriodKey].volume = Number(periodData[key as PeriodKey].volume.toFixed(2));
        });

        // console.log({ periodData });
        return periodData;
      })
    );
  }

  watchStates(): Observable<State[]> {
    console.log(`[DataService] watchStates started - isServer: ${this.platformService.isServer}`);
    
    if (this.platformService.isServer) {
      console.log(`[DataService] watchStates on server - using filesystem`);
      // Server side: use already loaded static data
      return of(this.statesData.getValue()).pipe(
        tap((states) => {
          console.log(`[DataService] watchStates server - using ${states.length} static states`);
          if (states.length > 0 && states[0].floor && this.stateSvc.getAggregatedFloor() === 0) {
            this.stateSvc.setAggregatedFloor(this.weiToEthPipe.transform(states[0].floor));
          }
        })
      );
    }
    
    // Client side: make HTTP and GraphQL calls
    const nowTimestamp = () => Math.floor(Date.now() / 1000);
    let statesData: State[] = [];
    const statesUrl = environment.collection === 'cryptopunks' ? `${environment.staticUrl}/_states.json` 
      : environment.collection === 'cryptopunks-v1' ? `/assets/_punksv1_states.json`
      : `/assets/_phunks_states.json`;
    
    console.log(`[DataService] watchStates client - fetching from: ${statesUrl}`);
    
    return this.http.get<State[]>(statesUrl).pipe(
      tap(() => console.log(`[DataService] watchStates HTTP request started`)),
      timeout(5000), // 5 seconds timeout for SSR
      tap((res: State[]) => {
        console.log(`[DataService] watchStates HTTP completed, states count:`, res?.length || 0);
        statesData = res;
        this.statesData.next(res);
      }),
      switchMap((res: State[]) => {
        console.log(`[DataService] watchStates starting GraphQL query`);
        // Use named client 'states' if available, otherwise fallback to default client
        const apolloClient = this.apollo.use('states') || this.apollo;
        console.log('[DataService] watchStates - Using Apollo client:', apolloClient === this.apollo ? 'default' : 'states (named)');
        console.log('[DataService] watchStates - Environment statesGraphURI:', environment.statesGraphURI);
        console.log('[DataService] watchStates - Environment graphURI:', environment.graphURI);
        
        return apolloClient.query({
          query: WATCH_STATES_DATA,
          variables: {
            first: 1000,
            lastTimestamp: (Number(res[0]?.timestamp) + 1) || 0,
            nowTimestamp: nowTimestamp(),
          },
        }).pipe(
          timeout(10000), // 10 seconds timeout for GraphQL
          tap(() => console.log(`[DataService] watchStates GraphQL query completed`)),
          catchError((error) => {
            console.error('[DataService] Error in watchStates GraphQL query:', error);
            if (error.name === 'TimeoutError') {
              console.warn('[DataService] watchStates GraphQL timeout reached, returning static data');
            }
            return of({ data: { states: [] } });
          })
        );
      }),
      map((res: any) => {
        const merged = [...statesData, ...res.data.states];
        
        const uniqueMerged = merged.filter((state, index, self) => 
          index === self.findIndex((s) => s.id === state.id)
        );
        uniqueMerged.sort((a, b) => Number(b.timestamp) - Number(a.timestamp));
        console.log('[DataService] watchStates final merged states count:', uniqueMerged.length);
        this.statesData.next(uniqueMerged);
        return uniqueMerged;
      }),
      catchError((error) => {
        console.error('[DataService] Error in watchStates:', error);
        if (error.name === 'TimeoutError') {
          console.warn('[DataService] watchStates HTTP timeout reached, returning empty array');
        }
        return of([]);
      })
    );
  }

  watchAllStates(): Observable<State[]> {
    const nowTimestamp = () => Math.floor(Date.now() / 1000);
    const pageSize = 1000;
    
    // Helper function to fetch a page of historical states
    const fetchHistoricalStates = (timestamp: number): Observable<State[]> => {
      // Use named client 'states' if available, otherwise fallback to default client
      const apolloClient = this.apollo.use('states') || this.apollo;
      console.log('[DataService] fetchHistoricalStates - Using Apollo client:', apolloClient === this.apollo ? 'default' : 'states (named)');
      console.log('[DataService] fetchHistoricalStates - Environment statesGraphURI:', environment.statesGraphURI);
      console.log('[DataService] fetchHistoricalStates - Environment graphURI:', environment.graphURI);
      
      return apolloClient.query({
        query: WATCH_STATES_DATA,
        variables: {
          first: pageSize,
          lastTimestamp: 0,
          nowTimestamp: timestamp,
        },
      }).pipe(
        map((res: any) => res.data.states as State[])
      );
    };
  
    // Recursive function to fetch all pages
    const fetchAllHistoricalStates = (
      timestamp: number,
      accumulated: State[] = []
    ): Observable<State[]> => {
      return fetchHistoricalStates(timestamp).pipe(
        switchMap((states) => {
          const allStates = [...accumulated, ...states];
          
          // If we got a full page, there might be more
          if (states.length === pageSize) {
            const oldestTimestamp = Math.min(...states.map(s => Number(s.timestamp)));
            return fetchAllHistoricalStates(oldestTimestamp, allStates);
          }
          
          // Sort all states by timestamp descending
          allStates.sort((a, b) => Number(b.timestamp) - Number(a.timestamp));
          return of(allStates);
        })
      );
    };
  
    // Start with historical data fetch, then fetch new states
    return fetchAllHistoricalStates(nowTimestamp()).pipe(
      tap((initialStates) => this.statesData.next(initialStates)),
      switchMap((initialStates) => {
        // Fetch new states after the most recent timestamp
        const mostRecentTimestamp = Number(initialStates[0]?.timestamp) + 1 || 0;
        
        // Use named client 'states' if available, otherwise fallback to default client
        const apolloClient = this.apollo.use('states') || this.apollo;
        console.log('[DataService] watchAllStates - Using Apollo client:', apolloClient === this.apollo ? 'default' : 'states (named)');
        console.log('[DataService] watchAllStates - Environment statesGraphURI:', environment.statesGraphURI);
        console.log('[DataService] watchAllStates - Environment graphURI:', environment.graphURI);
        
        return apolloClient.query({
          query: WATCH_STATES_DATA,
          variables: {
            first: pageSize,
            lastTimestamp: mostRecentTimestamp,
            nowTimestamp: nowTimestamp(),
          },
        }).pipe(
          map((res: any) => {
            const newStates = res.data.states as State[];
            const merged = [...newStates, ...initialStates];
            
            const uniqueMerged = merged.filter((state, index, self) => 
              index === self.findIndex((s) => s.id === state.id)
            );
            uniqueMerged.sort((a, b) => Number(b.timestamp) - Number(a.timestamp));
            console.log('uniqueMerged', uniqueMerged.length);
            this.statesData.next(uniqueMerged);
            return uniqueMerged;
          })
        );
      })
    );
  }

  ///////////////////////////////////////////////////////////////////////////////
  // EVENTS /////////////////////////////////////////////////////////////////////
  ///////////////////////////////////////////////////////////////////////////////

  fetchEvents(limit: number, skip: number, type?: EventType, order?: SubgraphSorts, orderDirection?: SubgraphSortDirections, usePolling: boolean = true): Observable<any> {
    console.log(`[DataService] fetchEvents started - isServer: ${this.platformService.isServer}, Apollo available:`, !!this.apollo);
    console.log(`[DataService] fetchEvents parameters:`, { limit, skip, type, order, orderDirection });
    
    if (!this.apollo) {
      console.warn('[DataService] Apollo not available for fetchEvents');
      return of([]);
    }

    console.log(`[DataService] fetchEvents started with limit: ${limit}, skip: ${skip}, type: ${type}`);
    
    const q: any = {
      variables: {
        skip,
        limit,
        order,
        orderDirection
      }
    };
    q.variables['minValue'] = -1;
    q.variables['toAccountNot'] = "0x00";
    
    if (type === 'All') {
      console.log(`[DataService] Using GET_EVENTS query for type='All'`);
      q.query = this.graphQLQueriesService.getQuery(environment.collection, 'GET_EVENTS');
      console.log(`[DataService] GET_EVENTS query:`, q.query);
    } else {
      console.log(`[DataService] Using GET_EVENTS_TYPE query for type:`, type);
      q.query = GET_EVENTS_TYPE;
      if (type == 'OfferedTo') {
        type = 'Offered'
        q.variables['toAccountNot'] = ZERO_ADDRESS;
        q.variables['minValue'] = 1;
      }
      q.variables['type'] = type;
    }

    console.log(`[DataService] fetchEvents final query config:`, {
      query: q.query?.loc?.source?.body || 'Query object',
      variables: q.variables,
      isServer: this.platformService.isServer
    });

    if (this.platformService.isServer) {
      // SSR: Use regular query (no polling on server)
      console.log('[DataService] fetchEvents on server - using regular query');
      const timeoutValue = 5000;
      console.log(`[DataService] fetchEvents using timeout:`, timeoutValue, 'ms');
      
      return this.apollo.query(q).pipe(
        take(1),
        tap(() => console.log(`[DataService] fetchEvents Apollo query started, params:`, { limit, skip, type, order, orderDirection })),
        timeout(timeoutValue),
        tap(() => console.log(`[DataService] fetchEvents GraphQL query completed successfully`)),
        map((result: any) => {
          console.log(`[DataService] fetchEvents raw result:`, {
            hasData: !!result?.data,
            hasEvents: !!result?.data?.events,
            eventsLength: result?.data?.events?.length || 0,
            events: result?.data?.events?.slice(0, 3) || []
          });
          return result.data.events as any[];
        }),
        catchError((error) => {
          console.error('[DataService] Error in fetchEvents:', {
            name: error.name,
            message: error.message,
            status: error.status,
            networkError: error.networkError,
            graphQLErrors: error.graphQLErrors,
            isTimeout: error.name === 'TimeoutError',
            isInjectorDestroyed: error.message?.includes('Injector has already been destroyed')
          });
          
          if (error.name === 'TimeoutError') {
            console.warn('[DataService] fetchEvents timeout reached, returning empty array');
          } else if (error.message?.includes('Injector has already been destroyed')) {
            console.warn('[DataService] Injector destroyed, returning empty array');
          }
          
          return of([]);
        })
      );
    } else {
      // Client: Use watchQuery for real-time updates or simple query for one-time requests
      if (usePolling) {
        console.log('[DataService] fetchEvents on client - using watchQuery with polling');
        
        return this.apollo.watchQuery({
          query: q.query,
          variables: q.variables,
          fetchPolicy: 'cache-and-network',
          errorPolicy: 'all',
          notifyOnNetworkStatusChange: true,
          pollInterval: pollInterval, // Poll every 30 seconds
        }).valueChanges.pipe(
        tap(() => console.log(`[DataService] fetchEvents watchQuery completed, params:`, { limit, skip, type, order, orderDirection })),
        map((result: any) => {
          console.log(`[DataService] fetchEvents watchQuery raw result:`, {
            hasData: !!result?.data,
            hasEvents: !!result?.data?.events,
            eventsLength: result?.data?.events?.length || 0,
            events: result?.data?.events?.slice(0, 3) || []
          });
          return result.data.events as any[];
        }),
        catchError((error) => {
          console.error('[DataService] Error in fetchEvents watchQuery:', {
            name: error.name,
            message: error.message,
            status: error.status,
            networkError: error.networkError,
            graphQLErrors: error.graphQLErrors,
            isTimeout: error.name === 'TimeoutError',
            isInjectorDestroyed: error.message?.includes('Injector has already been destroyed')
          });
          
          if (error.name === 'TimeoutError') {
            console.warn('[DataService] fetchEvents watchQuery timeout reached, returning empty array');
          } else if (error.message?.includes('Injector has already been destroyed')) {
            console.warn('[DataService] Injector destroyed, returning empty array');
          }
          
          return of([]);
        })
      );
      } else {
        // Use simple query without polling for loadMore operations
        console.log('[DataService] fetchEvents on client - using simple query (no polling)');
        
        const timeoutValue = 10000; // 10 seconds timeout for load more
        console.log(`[DataService] fetchEvents using timeout:`, timeoutValue, 'ms');
        
        return this.apollo.query(q).pipe(
          take(1),
          tap(() => console.log(`[DataService] fetchEvents Apollo query started, params:`, { limit, skip, type, order, orderDirection })),
          timeout(timeoutValue),
          tap(() => console.log(`[DataService] fetchEvents GraphQL query completed successfully`)),
          map((result: any) => {
            console.log(`[DataService] fetchEvents raw result:`, {
              hasData: !!result?.data,
              hasEvents: !!result?.data?.events,
              eventsLength: result?.data?.events?.length || 0,
              events: result?.data?.events?.slice(0, 3) || []
            });
            return result.data.events as any[];
          }),
          catchError((error) => {
            console.error('[DataService] Error in fetchEvents query:', {
              name: error.name,
              message: error.message,
              status: error.status,
              networkError: error.networkError,
              graphQLErrors: error.graphQLErrors,
              isTimeout: error.name === 'TimeoutError',
              isInjectorDestroyed: error.message?.includes('Injector has already been destroyed')
            });
            
            if (error.name === 'TimeoutError') {
              console.warn('[DataService] fetchEvents query timeout reached, returning empty array');
            } else if (error.message?.includes('Injector has already been destroyed')) {
              console.warn('[DataService] Injector destroyed, returning empty array');
            }
            
            return of([]);
          })
        );
      }
    }
  }


  clearSalesData() {
    this.salesData.next([])
  }

  loadMoreSalesData(): Observable<any> {
    const limit = 200;
    const currentSales = this.salesData.getValue();
    const skip = this.stateSvc.getSalesLoaded(); // Use total loaded count, not filtered count
    
    console.log('[DataService] loadMoreSalesData - skip:', skip, 'currentSales.length:', currentSales.length);
    
    // Get current marketplace filter
    const currentFilter = this.stateSvc.getMarketplaceFilter();
    console.log('[DataService] loadMoreSalesData - current marketplace filter:', currentFilter);
    
    // Use a simple query instead of watchQuery to avoid cancellation issues
    const useAlternativeSales = !!environment.featureFlags?.NEW_GRAPH_FOR_SALE_HISTORY;
    if (useAlternativeSales) {
      const currentSort = this.stateSvc.currentSort;
      const collections = [
        environment.collection === 'cryptopunks' ? '0xb47e3cd837ddf8e4c57f05d70ab865de6e193bbb' : environment.collection,
        environment.wrappedPunksAddress,
        environment.c721Address
      ];
      const mapFilterToPlatform = (filter: any): string | undefined => {
        if (!filter || filter === 'All') return undefined;
        if (filter === 'Native') return 'native';
        if (filter === 'Opensea') return 'opensea';
        if (filter === 'Blur') return 'blurio';
        if (filter === 'NFTX') return 'nftx';
        if (filter === 'Gondi') return 'gondi';
        return undefined;
      };
      const txFilterForStrategy = (filter: any): string | undefined => {
        if (filter === 'PunkStrategy') return TOKENWORKS_ADDRESS.toLowerCase();
        if (filter === 'PunkAuction') return PUNKAUCTION_ADDRESS.toLowerCase();
        if (filter === 'CigStrategy') return '0x0000000000000000000000000000000000000001';
        return undefined;
      };
      const orderBy = `${currentSort.sort}:${currentSort.direction}`;

      const query = this.graphQLQueriesService.getQuery(environment.collection, 'GET_SALES_HISTORY');
      const mapper = this.graphQLQueriesService.getMapper(environment.collection, 'GET_SALES_HISTORY');
      return this.apollo.use('alternative').query<any>({
        query,
        variables: {
          collections,
          orderBy,
          platform: mapFilterToPlatform(currentFilter),
          skip,
          tokenId: undefined,
          txFromOrTxToEq: txFilterForStrategy(currentFilter),
          eventType: 'sale'
        },
        fetchPolicy: 'network-only',
        errorPolicy: 'all'
      }).pipe(
        map((res: ApolloQueryResult<any>) => {
          const events = res.data?.getAllEvents || [];
          return mapper ? mapper({ getAllEvents: events }) : events;
        }),
        map((res) => res.map((item: any) => ({ ...item, id: item.tokenId }))),
        tap((originalRes) => {
          console.log('[DataService] loadMoreSalesData (alt) - original count from server:', originalRes.length);
          this.stateSvc.setSalesLoaded(skip + originalRes.length);
        }),
        switchMap((res) => this.addAttributes(res)),
        tap((res) => {
          if (res.length > 0) {
            const existingIds = new Set(currentSales.map(sale => sale.id));
            const newItems = res.filter(item => !existingIds.has(item.id));
            if (newItems.length > 0) this.salesData.next([...currentSales, ...newItems]);
          }
        }),
        tap((res) => this.stateSvc.setLoadingMore(false)),
        take(1)
      );
    }

    return this.fetchEvents(limit, skip, 'Sale', this.stateSvc.currentSort.sort, this.stateSvc.currentSort.direction, false).pipe(
      map((res) => res.map((item: any) => ({
        ...item,
        id: item.tokenId,
      }))),
      // Capture the original count before filtering
      tap((originalRes) => {
        console.log('[DataService] loadMoreSalesData - original count from server:', originalRes.length);
        // Always update the skip counter to reflect the total number of items loaded from server
        this.stateSvc.setSalesLoaded(skip + originalRes.length);
      }),
      // Apply platform filter
      map((res) => {
        if (currentFilter && currentFilter !== 'All') {
          console.log('[DataService] Applying platform filter in loadMoreSalesData:', currentFilter);
          return res.filter((item: any) => {
            if (currentFilter === 'Native') return !item.platform || item.platform === 'native';
            if (currentFilter === 'Opensea') return item.platform === 'opensea';
            if (currentFilter === 'Blur') return item.platform === 'blur';
            if (currentFilter === 'NFTX') return item.platform === 'nftx';
            if (currentFilter === 'Gondi') return item.platform === 'gondi';
            return true;
          });
        }
        return res;
      }),
      switchMap((res) => this.addAttributes(res)),
      tap((res) => {
        if (res.length > 0) {
          // Remove duplicates by checking if items already exist in currentSales
          const existingIds = new Set(currentSales.map(sale => sale.id));
          const newItems = res.filter(item => !existingIds.has(item.id));
          
          console.log('[DataService] loadMoreSalesData - filtered duplicates:', {
            originalCount: res.length,
            newItemsCount: newItems.length,
            duplicatesRemoved: res.length - newItems.length
          });
          
          if (newItems.length > 0) {
            this.salesData.next([...currentSales, ...newItems]);
          }
        }
      }),
      tap((res) => this.stateSvc.setLoadingMore(false)),
      take(1) // Ensure it completes after first emission
    );
  }

  getSalesData(): Observable<any> {
    const limit = 200;
    console.log('[DataService] getSalesData called - isServer:', this.platformService.isServer);
    this.stateSvc.setMarketLoading(true)
    
    // Get current marketplace filter
    const currentFilter = this.stateSvc.getMarketplaceFilter();
    console.log('[DataService] getSalesData - current marketplace filter:', currentFilter);
    const useAlternativeSales = !!environment.featureFlags?.NEW_GRAPH_FOR_SALE_HISTORY;
    const currentSort = this.stateSvc.currentSort;
    const collections = [
      environment.collection === 'cryptopunks' ? '0xb47e3cd837ddf8e4c57f05d70ab865de6e193bbb' : environment.collection,
      environment.wrappedPunksAddress,
      environment.c721Address
    ];
    const mapFilterToPlatform = (filter: any): string | undefined => {
      if (!filter || filter === 'All') return undefined;
      if (filter === 'Native') return 'native';
      if (filter === 'Opensea') return 'opensea';
      if (filter === 'Blur') return 'blurio';
      if (filter === 'NFTX') return 'nftx';
      if (filter === 'Gondi') return 'gondi';
      return undefined;
    };
    const txFilterForStrategy = (filter: any): string | undefined => {
      if (filter === 'PunkStrategy') return TOKENWORKS_ADDRESS.toLowerCase();
      if (filter === 'PunkAuction') return PUNKAUCTION_ADDRESS.toLowerCase();
      if (filter === 'CigStrategy') return '0x0000000000000000000000000000000000000001';
      return undefined;
    };
    const orderBy = `${currentSort.sort}:${currentSort.direction}`;
    
    // On server, only load first page and stop recursion
    if (this.platformService.isServer) {
      console.log('[DataService] getSalesData on server - loading first page only');
      if (useAlternativeSales) {
        const query = this.graphQLQueriesService.getQuery(environment.collection, 'GET_SALES_HISTORY');
        const mapper = this.graphQLQueriesService.getMapper(environment.collection, 'GET_SALES_HISTORY');
        return this.apollo.use('alternative').query<any>({
          query,
          variables: {
            collections,
            orderBy,
            platform: mapFilterToPlatform(currentFilter),
            skip: 0,
            tokenId: undefined,
            txFromOrTxToEq: txFilterForStrategy(currentFilter),
            eventType: 'sale'
          },
          fetchPolicy: 'network-only',
          errorPolicy: 'all'
        }).pipe(
          map((res: ApolloQueryResult<any>) => {
            const events = res.data?.getAllEvents || [];
            return mapper ? mapper({ getAllEvents: events }) : events;
          }),
          map((res) => res.map((item: any) => ({ ...item, id: item.tokenId }))),
          switchMap((res) => this.addAttributes(res)),
          tap((res) => {
            console.log('[DataService] altGraph addAttributes server result:', res?.length || 0, 'items');
            this.salesData.next(res);
          }),
          tap((res) => this.stateSvc.setSalesLoaded(res.length)),
          tap((res) => this.stateSvc.setMarketLoading(false)),
          tap((res) => this.stateSvc.setLoadingMore(false)),
          take(1)
        );
      } else {
        return this.fetchEvents(limit, 0, 'Sale', this.stateSvc.currentSort.sort, this.stateSvc.currentSort.direction).pipe(
          tap((res) => console.log('[DataService] fetchEvents server result:', res?.length || 0, 'items')),
          map((res) => res.map((item: any) => ({
            ...item,
            id: item.tokenId,
          }))),
          // Apply platform filter on server side
          map((res) => {
            if (currentFilter && currentFilter !== 'All') {
              console.log('[DataService] Applying platform filter on server:', currentFilter);
              return res.filter((item: any) => {
                if (currentFilter === 'Native') return !item.platform || item.platform === 'native';
                if (currentFilter === 'Opensea') return item.platform === 'opensea';
                if (currentFilter === 'Blur') return item.platform === 'blur';
                if (currentFilter === 'NFTX') return item.platform === 'nftx';
                if (currentFilter === 'Gondi') return item.platform === 'gondi';
                return true;
              });
            }
            return res;
          }),
          switchMap((res) => this.addAttributes(res)),
          tap((res) => {
            console.log('[DataService] addAttributes server result:', res?.length || 0, 'items');
            console.log('[DataService] About to call salesData.next() with:', res?.length || 0, 'items');
            this.salesData.next(res);
            console.log('[DataService] salesData.next() called, current salesData value:', this.salesData.getValue()?.length || 0, 'items');
          }),
          tap((res) => this.stateSvc.setSalesLoaded(res.length)),
          tap((res) => this.stateSvc.setMarketLoading(false)),
          tap((res) => this.stateSvc.setLoadingMore(false)),
          take(1) // Ensure it completes after first emission
        );
      }
    }
    
    // On client, load first page only to avoid infinite loops
    console.log('[DataService] getSalesData on client - loading first page only to avoid loops');
    if (useAlternativeSales) {
      const query = this.graphQLQueriesService.getQuery(environment.collection, 'GET_SALES_HISTORY');
      const mapper = this.graphQLQueriesService.getMapper(environment.collection, 'GET_SALES_HISTORY');
      return this.apollo.use('alternative').query<any>({
        query,
        variables: {
          collections,
          orderBy,
          platform: mapFilterToPlatform(currentFilter),
          skip: 0,
          tokenId: undefined,
          txFromOrTxToEq: txFilterForStrategy(currentFilter),
          eventType: 'sale'
        },
        fetchPolicy: 'network-only',
        errorPolicy: 'all'
      }).pipe(
        map((res: ApolloQueryResult<any>) => {
          const events = res.data?.getAllEvents || [];
          return mapper ? mapper({ getAllEvents: events }) : events;
        }),
        map((res) => res.map((item: any) => ({ ...item, id: item.tokenId }))),
        switchMap((res) => this.addAttributes(res)),
        tap((res) => {
          console.log('[DataService] altGraph addAttributes client result:', res?.length || 0, 'items');
          this.salesData.next(res);
        }),
        tap((res) => this.stateSvc.setSalesLoaded(res.length)),
        tap((res) => this.stateSvc.setMarketLoading(false)),
        tap((res) => this.stateSvc.setLoadingMore(false)),
        take(1)
      );
    } else {
      return this.fetchEvents(limit, 0, 'Sale', this.stateSvc.currentSort.sort, this.stateSvc.currentSort.direction).pipe(
        tap((res) => console.log('[DataService] fetchEvents client result:', res?.length || 0, 'items')),
        map((res) => res.map((item: any) => ({
          ...item,
          id: item.tokenId,
        }))),
        // Apply platform filter on client side
        map((res) => {
          if (currentFilter && currentFilter !== 'All') {
            console.log('[DataService] Applying platform filter on client:', currentFilter);
            return res.filter((item: any) => {
              if (currentFilter === 'Native') return !item.platform || item.platform === 'native';
              if (currentFilter === 'Opensea') return item.platform === 'opensea';
              if (currentFilter === 'Blur') return item.platform === 'blur';
              if (currentFilter === 'NFTX') return item.platform === 'nftx';
              if (currentFilter === 'Gondi') return item.platform === 'gondi';
              if (currentFilter === 'PunkStrategy') return (item.fromAccount?.id === TOKENWORKS_ADDRESS.toLowerCase() || item.toAccount?.id === TOKENWORKS_ADDRESS.toLowerCase());
              if (currentFilter === 'PunkAuction') return (item.fromAccount?.id === PUNKAUCTION_ADDRESS.toLowerCase() || item.toAccount?.id === PUNKAUCTION_ADDRESS.toLowerCase());
              if (currentFilter === 'CigStrategy') return false
              return true;
            });
          }
          return res;
        }),
        switchMap((res) => this.addAttributes(res)),
        tap((res) => {
          console.log('[DataService] addAttributes client result:', res?.length || 0, 'items');
          console.log('[DataService] About to call salesData.next() with:', res?.length || 0, 'items');
          this.salesData.next(res);
          console.log('[DataService] salesData.next() called, current salesData value:', this.salesData.getValue()?.length || 0, 'items');
        }),
        tap((res) => this.stateSvc.setSalesLoaded(res.length)),
        tap((res) => this.stateSvc.setMarketLoading(false)),
        tap((res) => this.stateSvc.setLoadingMore(false)),
        take(1) // Ensure it completes after first emission
      );
    }
  }

  fetchSoldEvents(address: string, limit?: number): Observable<any> {
    const formattedAddress = address.toLowerCase();
    const queryLimit = limit || 1000;

    // Check if we should use the alternative GraphQL endpoint
    const useAlternativeEndpoint = environment.featureFlags?.USE_OUR_SALES_INDEXER__FOR_DASHBOARD_DATA || false;

    if (useAlternativeEndpoint && environment.alternativeGraphURI) {
      const collections = buildDashboardSaleCollections();

      const query = this.graphQLQueriesService.getQuery(environment.collection, 'GET_USER_SENT_SALES');
      const mapper = this.graphQLQueriesService.getMapper(environment.collection, 'GET_USER_SENT_SALES');

      return this.apollo.use('alternative').query<any>({
        query,
        variables: {
          collections,
          txFrom: formattedAddress,
          eventType: 'sale'
        },
        fetchPolicy: 'network-only',
        errorPolicy: 'all'
      }).pipe(
        map((res: ApolloQueryResult<any>) => {
          const events = res.data?.getAllEvents || [];
          return mapper ? mapper({ getAllEvents: events }) : events;
        }),
        map((sentEvents) => sentEvents.slice(0, queryLimit)), // Limit results client-side
        switchMap(sentEvents => {
          sentEvents = sentEvents.map((re: any) => ({
            ...re,
            wrapped: isDashboardSaleWrapped(re),
          }));
          return of(sentEvents)
        }),
        map((sentEvents) => [...sentEvents].sort((a, b) => +a.blockTimestamp - +b.blockTimestamp)),
      );
    }

    // Original implementation using default GraphQL endpoint
    const fetchEvents = (query: any) => {
      return this.apollo.query({
        query: query,
        variables: {
          limit: queryLimit,
          address: formattedAddress,
        },
      }).pipe(
        map((result: any) => result.data.events as any[]),
      );
    };

    const sentEvents$ = fetchEvents(GET_USER_SENT_SALES);
  
    return sentEvents$.pipe(
      switchMap(sentEvents => {
        sentEvents = sentEvents.map(re => ({
          ...re,
          wrapped: isDashboardSaleWrapped(re),
        }))
        return of(sentEvents)
      }),      
      map((sentEvents) => [...sentEvents].sort((a, b) => a.blockTimestamp - b.blockTimestamp)),
    );
  }

  getEthFollowStatsFor(ens: string): Observable<EthFollowStats> {
    return this.http.get<EthFollowStats>(`https://api.ethfollow.xyz/api/v1/users/${ens}/stats`);
  }

  fetchBoughtEvents(address: string, limit?: number): Observable<any> {
    const formattedAddress = address.toLowerCase();
    const queryLimit = limit || 1000;

    // Check if we should use the alternative GraphQL endpoint
    const useAlternativeEndpoint = environment.featureFlags?.USE_OUR_SALES_INDEXER__FOR_DASHBOARD_DATA || false;

    if (useAlternativeEndpoint && environment.alternativeGraphURI) {
      const collections = buildDashboardSaleCollections();

      const query = this.graphQLQueriesService.getQuery(environment.collection, 'GET_USER_RECEIVED_SALES');
      const mapper = this.graphQLQueriesService.getMapper(environment.collection, 'GET_USER_RECEIVED_SALES');

      return this.apollo.use('alternative').query<any>({
        query,
        variables: {
          collections,
          txTo: formattedAddress,
          eventType: 'sale'
        },
        fetchPolicy: 'network-only',
        errorPolicy: 'all'
      }).pipe(
        map((res: ApolloQueryResult<any>) => {
          const events = res.data?.getAllEvents || [];
          return mapper ? mapper({ getAllEvents: events }) : events;
        }),
        map((receivedEvents) => receivedEvents.slice(0, queryLimit)), // Limit results client-side
        switchMap(receivedEvents => {
          receivedEvents = receivedEvents.map((re: any) => ({
            ...re,
            wrapped: isDashboardSaleWrapped(re),
          }));
          return of(receivedEvents)
        }),
        map((receivedEvents) => [...receivedEvents].sort((a, b) => +a.blockTimestamp - +b.blockTimestamp)),
        tap((receivedEvents) => console.log('[DataService] fetchBoughtEvents result:', receivedEvents)),
      );
    }
  
    // Original implementation using default GraphQL endpoint
    const fetchEvents = (query: any) => {
      return this.apollo.query({
        query: query,
        variables: {
          limit: queryLimit,
          address: formattedAddress,
        },
      }).pipe(
        map((result: any) => (result.data?.events ?? [])as any[]),
      );
    };
  
    const receivedEvents$ = fetchEvents(GET_USER_RECEIVED_SALES);
  
    // Merge the results
    return receivedEvents$.pipe(
      switchMap(receivedEvents => {
        receivedEvents = receivedEvents.map(re => {
          return {...re, wrapped: isDashboardSaleWrapped(re)}
        })
        return of(receivedEvents)
      }),
      map((receivedEvents) => [ ...receivedEvents].sort((a, b) => a.blockTimestamp - b.blockTimestamp)),
      tap((receivedEvents) => console.log('[DataService] fetchBoughtEvents result:', receivedEvents)),
    );
  }

  ///////////////////////////////////////////////////////////////////////////////
  // TOP SALES //////////////////////////////////////////////////////////////////
  ///////////////////////////////////////////////////////////////////////////////

  fetchTopSales(limit: number, period: Period, hideFlashLoans = true): Observable<any> {
    console.log(`[DataService] fetchTopSales started - isServer: ${this.platformService.isServer}`);

    const useIndexerLargest = !!environment.featureFlags?.NEW_GRAPH_FOR_LARGEST_SALES;
    if (useIndexerLargest && environment.alternativeGraphURI) {
      let baseUrl = environment.alternativeGraphURI;
      if (baseUrl.endsWith('/graphql')) {
        baseUrl = baseUrl.replace(/\/graphql$/, '');
      }
      const effectiveLimit = hideFlashLoans ? limit + environment.flaggedTransactions.length : limit;
      const collections = [
        environment.collection === 'cryptopunks' ? '0xb47e3cd837ddf8e4c57f05d70ab865de6e193bbb' : environment.collection,
        environment.wrappedPunksAddress,
        environment.c721Address,
      ].filter((c) => !!c && c !== '0x0000000000000000000000000000000000000000');

      const params: Record<string, string> = {
        period: period.key,
        limit: String(Math.min(100, effectiveLimit)),
        collections: collections.join(','),
      };

      return this.http.get<any[]>(`${baseUrl}/events/sales/largest`, { params }).pipe(
        map((rows) => {
          const mapper = this.graphQLQueriesService.getMapper(environment.collection, 'GET_SALES_HISTORY');
          const events = mapper ? mapper({ getAllEvents: rows || [] }) : rows || [];
          if (hideFlashLoans) {
            return events
              .filter((e: any) => !environment.flaggedTransactions.includes(String(e.transactionHash || '').toLowerCase()))
              .slice(0, limit);
          }
          return (events as any[]).slice(0, limit);
        }),
        catchError((error) => {
          console.error('[DataService] Error in fetchTopSales (indexer REST):', error);
          return of([]);
        }),
      );
    }

    if (!this.apollo) {
      console.warn('[DataService] Apollo not available for fetchTopSales');
      return of([]);
    }

    console.log(`[DataService] fetchTopSales started with limit: ${limit}, period:`, period);
    
    const variables: any = {
      limit: hideFlashLoans ? limit + environment.flaggedTransactions.length : limit,
      period: period.key !== 'all' 
        ? Math.floor(Date.now() / 1000 - period.limit * 86400)
        : 0
    };

    console.log(`[DataService] fetchTopSales variables:`, variables);

    // Use query for both SSR and client
    return this.apollo.query({
      query: this.graphQLQueriesService.getQuery(environment.collection, 'GET_TOP_SALES'),
      variables,
    }).pipe(
      timeout(30000), // 30 seconds timeout to give more time
      tap(() => console.log(`[DataService] fetchTopSales GraphQL query completed`)),
      map((result: any) => {
        console.log(`[DataService] fetchTopSales raw result:`, result?.data?.events?.length || 0, 'events');
        return result.data.events;
      }),
      map(events => {
        if (hideFlashLoans) {
          const filtered = events
            .filter((event: any) => !environment.flaggedTransactions.includes(event.transactionHash))
            .slice(0, limit);
          console.log(`[DataService] fetchTopSales filtered result:`, filtered.length, 'events');
          return filtered;
        }
        console.log(`[DataService] fetchTopSales final result:`, events?.length || 0, 'events');
        return events;
      }),
      catchError((error) => {
        console.error('[DataService] Error in fetchTopSales:', error);
        if (error.name === 'TimeoutError') {
          console.warn('[DataService] fetchTopSales timeout reached, returning empty array');
        }
        return of([]);
      })
    );
  }

  ///////////////////////////////////////////////////////////////////////////////
  // SINGLE PUNK ////////////////////////////////////////////////////////////////
  ///////////////////////////////////////////////////////////////////////////////

  fetchSinglePunk(id: string): Observable<Punk> {
    console.log(`[DataService] fetchSinglePunk started for id: ${id}`);
    return this.marketData$.pipe(
      //filter(r => r && r.length > 1),
      take(1),
      tap(() => console.log(`[DataService] marketData$ received, starting GraphQL query for punk ${id}`)),
      switchMap(() => {
        // Use apollo.query() for both SSR and client
        return this.apollo.query<{ punk: Punk }>({
          query: this.graphQLQueriesService.getQuery(environment.collection, 'GET_PUNK'),
          variables: { id },
        }).pipe(
          timeout(15000), // 15 seconds timeout
          map((res: ApolloQueryResult<{ punk: Punk }>) => {
            console.log(`[DataService] GraphQL query completed for punk ${id}:`, res.data?.punk ? 'SUCCESS' : 'NO DATA');
            return res.data?.punk || null
          }),
          map((punk: Punk) => {
            if (!environment.production && (punk.id === '1' || punk.id === '2' || punk.id === '3' || punk.id === '5170' || punk.id === '6596')) {
              punk = { ...punk };
              punk.owner = {
                id: '0x516Fc698fb46506aA983a14F40b30c908d86Dc82',
                punks: []
              };
            }
            return punk;
          })
        );
      }),
      map(p => {
        if (!p) return {id, attributes: []};
        if (p.listing) return p
        // or check for a gondi listing
        return {
          ...p,
          listing: this.gondiListings.has(p.id) ? this.gondiListings.get(p.id) : null
        }
      }),
      switchMap((res: Punk) => this.applySalesIndexerOwnership(res)),
      switchMap((res: Punk) => {
        console.log(`[DataService] Starting addAttributes for punk ${id}`);
        return this.addAttributes([res]);
      }),
      map((res) => {
        console.log(`[DataService] fetchSinglePunk completed for punk ${id}`);
        return {
          ...res[0],
          attributes: res[0].attributes.sort((a: Attribute, b: Attribute) => {
            if (a.k === "Sex") return -1;
            if (b.k === "Sex") return 1;
            return 0;
          }),
        };
      }),
      catchError((err) => {
        console.log(err);
        return EMPTY;
      })
    );
  }

  fetchSinglePunkListing(id: string): Observable<any> {
    return forkJoin([
      // this.fetchSingleReservoirListing(id),
      this.fetchNftxVaultListing(id),
    ]).pipe(
      map(([/*res, */nftx]) => /*res || */nftx),
      catchError((error) => {
        console.error('Error fetching listingsCall:', error);
        return EMPTY;
      })
    );
  }

  ///////////////////////////////////////////////////////////////////////////////
  // Reservoir //////////////////////////////////////////////////////////////////

  fetchAllReservoirListing(id: string): Observable<any> {
    console.log(`[DataService] fetchAllReservoirListing started for id: ${id}`);
    
    // Check if we should use custom indexer instead of Reservoir
    if (environment.featureFlags?.USE_ALTERNATIVE_LISTINGS_API) {
      console.log(`[DataService] Using custom indexer API for listings for tokenId: ${id}`);
      
      // Map collection to the correct slug for the GraphQL API
      const getCollectionSlug = (collection: string): string => {
        switch (collection) {
          case 'cryptophunks':
            return 'crypto-phunks';
          case 'cryptopunks':
            return 'wrapped-cryptopunks';
          case 'cryptopunks-v1':
            return 'official-v1-punks';
          default:
            console.warn(`[DataService] Unknown collection: ${collection}, using crypto-phunks as fallback`);
            return 'crypto-phunks';
        }
      };

      const collectionSlug = getCollectionSlug(environment.collection);
      console.log(`[DataService] Using collection slug: ${collectionSlug} for collection: ${environment.collection}`);

      const query = gql`
        query GetListingsByCollection($collectionSlug: String!, $tokenId: String!) {
          listingsByCollection(
            collectionSlug: $collectionSlug
            tokenId: $tokenId
            sort: {
              field: PRICE_AMOUNT
              order: ASC
            }
          ) {
            id
            tokenId
            priceAmount
            currentOwner
            platform
          }
        }
      `;

      const graphUrl = environment.alternativeListingsGraphURI || 'http://localhost:3000/graphql';
      console.log(`[DataService] Using GraphQL URL: ${graphUrl}`);

      return this.http.post(graphUrl, {
        query: query.loc?.source?.body || query,
        variables: {
          collectionSlug: collectionSlug,
          tokenId: id
        }
      }, {
        headers: {
          'Content-Type': 'application/json'
        }
      }).pipe(
        tap(() => console.log(`[DataService] Custom indexer GraphQL API call started for tokenId: ${id}`)),
        timeout(15000), // 15 seconds timeout
        map((res: any) => {
          const listings = res.data?.listingsByCollection || [];
          console.log(`[DataService] Custom indexer GraphQL API completed for tokenId: ${id}, listings count:`, listings.length);
          
          // Transform custom indexer data to match Reservoir format
          const contractAddress = environment.wrappedPunksAddress;
          
          // Map platform to source properties
          const getSourceProperties = (platform: string, tokenId: string) => {
            switch (platform) {
              case 'Blur':
                return {
                  domain: 'blur.io',
                  name: 'Blur',
                  icon: 'https://blur.io/favicons/180.png',
                  url: `https://blur.io/asset/${contractAddress}/${tokenId}`
                };
              case 'OpenSea':
                return {
                  domain: 'opensea.io',
                  name: 'OpenSea',
                  icon: '/images/opensea-logo.svg',
                  url: `https://opensea.io/assets/ethereum/${contractAddress}/${tokenId}`
                };
              default:
                return {
                  domain: platform?.toLowerCase() || 'custom-indexer',
                  name: platform || 'external marketplace',
                  icon: '',
                  url: ''
                };
            }
          };

          // Deduplicate by domain (similar to Reservoir API logic)
          const seenDomains = new Set();
          return listings
            .filter((listing: any) => {
              const sourceProps = getSourceProperties(listing.platform, listing.tokenId);
              const domain = sourceProps.domain;
              if (!domain || seenDomains.has(domain)) {
                return false;
              }
              seenDomains.add(domain);
              return true;
            })
            .map((listing: any) => {
              const sourceProps = getSourceProperties(listing.platform, listing.tokenId);
              return {
                value: this.ethToWei(listing.priceAmount || '0'), // Convert ETH to Wei
                source: sourceProps,
                maker: listing.currentOwner || '0x0000000000000000000000000000000000000000',
                validFrom: new Date().toISOString(),
                validUntil: null
              };
            });
        }),
        catchError((error) => {
          console.error('[DataService] Error fetching listings from custom indexer:', error);
          if (error.name === 'TimeoutError') {
            console.warn('[DataService] Custom indexer listings timeout reached, returning empty array');
          }
          return of([]);
        })
      );
    } else {
      // Use old Reservoir API approach
      const params = {
        token: `${environment.wrappedPunksAddress}:${id}`,
        sortBy: 'price',
        sortDirection: 'asc',
        limit: 50,
      };

      return this.http.get(`https://api.reservoir.tools/tokens/${environment.wrappedPunksAddress}:${id}/asks/v1`, {params}).pipe(
        timeout(15000), // 15 seconds timeout
        tap(() => console.log(`[DataService] Reservoir API call started for id: ${id}`)),
        map((result: any) => {
          const seenDomains = new Set();
          return result.orders.filter((order: any) => {
            const domain = order.source?.domain;
            if (!domain || seenDomains.has(domain)) {
              return false;
            }
            seenDomains.add(domain);
            return true;
          });        
        }),
        map((res:any) => {
          console.log(`[DataService] Reservoir API call completed for id: ${id}, orders count:`, res?.length || 0);
          const orders = res || [];
          return orders.map((listing: any) => ({
            value: listing?.price?.amount?.raw,
            source: listing?.source,
            maker: listing?.maker,
            validFrom: listing?.validFrom,
            validUntil: listing?.validUntil,
          })) || []
        }),
        catchError((error) => {
          console.error('Error fetching all listings:', error);
          return of([]);
        })
      );
    }
  }

  fetchSingleReservoirListing(id: string): Observable<any> {
    const params = {
      tokens: `${environment.wrappedPunksAddress}:${id}`,
      sortBy: 'floorAskPrice',
      sortDirection: 'desc',
      limit: 1,
    };

    return this.http.get(`https://api.reservoir.tools/tokens/v6`, {params}).pipe(
      map((res: any) => res.tokens[0]),
      map((res) => {
        const market = res?.market;
        const token = res?.token;
        return {
          id,
          value: market?.floorAsk?.price?.amount?.raw,
          source: market?.floorAsk?.source,
          wrappedOwner: token?.owner,
        };
      }),
      catchError((error) => {
        console.error('Error fetching listingsCall:', error);
        return EMPTY;
      })
    );
  }

  ///////////////////////////////////////////////////////////////////////////////
  // CIG ////////////////////////////////////////////////////////////////////////
  ///////////////////////////////////////////////////////////////////////////////

  fetchCigPrice(): Observable<any> {

    // if (!environment.production) return of(0);
    return this.http.get(`https://api.geckoterminal.com/api/v2/networks/eth/tokens/0xcb56b52316041a62b6b5d0583dce4a8ae7a3c629`).pipe(
      // tap((res) => console.log({res})),
      map((res: any) => ({price:Number(res?.data?.attributes?.price_usd) || 0, marketCap: res?.data?.attributes?.market_cap_usd})),
      catchError((error) => {
        console.error('fetchCigPrice:', error);
        return of(0);
      })
    );

    // Old coingecko API
    // if (!environment.production) return of(0);
    // return this.http.get(`https://api.coingecko.com/api/v3/coins/cigarette-token`, {
    //   params: {
    //     localization: false,
    //     tickers: false,
    //     market_data: true,
    //     community_data: false,
    //     developer_data: false,
    //     sparkline: false
    //   }
    // }).pipe(map((res: any) => res?.market_data?.current_price?.usd || 0));
  }

  fetchCigClaim(punkIndex: string): Observable<any> {
    console.log(`[DataService] fetchCigClaim started for punkIndex: ${punkIndex}`);
    if (environment.collection === 'cryptophunks') return of(null);
    return this.apollo.use('cig').query({
      query: GET_CIG_CLAIM,
      variables: { punkIndex },
    }).pipe(
      map((res: any) => res.data?.claims?.[0] || null),
      catchError((error) => {
        console.error('Error fetching cigClaim:', error);
        return of(null);
      })
    );
  }

  private buildAuthHeaders(): { [key: string]: string } {
    const headers: { [key: string]: string } = {};
    const jwtToken = this.stateSvc.getJwtToken();
    if (jwtToken) {
      headers['Authorization'] = `Bearer ${jwtToken}`;
    }
    const mimicAddress = this.stateSvc.getMimicAddress();
    if (mimicAddress) {
      headers['X-Mimic-As'] = mimicAddress;
    }
    return headers;
  }

  fetchCollectionBids(punkId: string): Observable<any[]> {
    console.log(`[DataService] fetchCollectionBids started for punkId: ${punkId}`);
    // Extract base URL from alternativeListingsGraphURI
    const graphUri = environment.alternativeListingsGraphURI || '';
    //cleanup that later
    const baseUrl = graphUri.replace(/\/graphql$/, '');
    
    // Determine source filter based on environment collection
    const getBidSource = (collection: string): string => {
      if (collection === 'cryptopunks') return 'cryptopunks.eth.limo,cryptopunks.app';
      if (collection === 'cryptopunks-v1') return 'v1cryptopunks.com';
      return 'cryptopunks.eth.limo'; // fallback
    };
    const filterSource = getBidSource(environment.collection);
    const apiUrl = `${baseUrl}/api/scraper/punk-bids/punk/${punkId}?filterSource=${encodeURIComponent(filterSource)}`;
    
    const headers = this.buildAuthHeaders();
    
    return this.http.get<any[]>(apiUrl, { headers: Object.keys(headers).length > 0 ? headers : undefined }).pipe(
      tap(() => console.log(`[DataService] Collection bids API call started for punkId: ${punkId}`)),
      timeout(5000), // 5 seconds timeout for SSR
      map((res: any) => {
        console.log(`[DataService] Collection bids API completed, bids count:`, res?.length || 0);
        // Filter only pending bids
        return (res.bids || []).filter((bid: any) => bid.status === 'pending');
      }),
      catchError((error) => {
        console.error('Error fetching collection bids:', error);
        return of([]);
      })
    );
  }

  /**
   * Fetch collection bid details by bid ID to get proof information
   * @param bidId The unique identifier of the bid
   * @returns Observable with bid details including proofs for each punk
   */
  fetchCollectionBidDetails(bidId: string): Observable<any> {
    console.log(`[DataService] fetchCollectionBidDetails started for bidId: ${bidId}`);
    // Extract base URL from alternativeListingsGraphURI
    const graphUri = environment.alternativeListingsGraphURI || '';
    const baseUrl = graphUri.replace(/\/graphql$/, '');
    const apiUrl = `${baseUrl}/api/scraper/punk-bids/${bidId}`;

    // Get JWT token if available (for confidential bids)
    const jwtToken = this.stateSvc.getJwtToken();

    // Prepare headers
    const headers: { [key: string]: string } = {};
    if (jwtToken) {
      headers['Authorization'] = `Bearer ${jwtToken}`;
    }

    return this.http.get<any>(apiUrl, { headers: Object.keys(headers).length > 0 ? headers : undefined }).pipe(
      tap(() => console.log(`[DataService] Collection bid details API call started for bidId: ${bidId}`)),
      timeout(5000), // 5 seconds timeout
      tap((res: any) => {
        console.log(`[DataService] Collection bid details API completed for bidId: ${bidId}`);
      }),
      catchError((error) => {
        console.error('Error fetching collection bid details:', error);
        return of(null);
      })
    );
  }

  /**
   * Fetch bid details with parsed punk indices
   * @param bidId The unique identifier of the bid
   * @returns Observable with bid details including parsed_punk_indices
   */
  fetchBidDetailsWithPunkIndices(bidId: string): Observable<any> {
    console.log(`[DataService] fetchBidDetailsWithPunkIndices started for bidId: ${bidId}`);
    // Extract base URL from alternativeListingsGraphURI
    const graphUri = environment.alternativeListingsGraphURI || '';
    const baseUrl = graphUri.replace(/\/graphql$/, '');
    
    // Get JWT token if available (for confidential bids)
    const jwtToken = this.stateSvc.getJwtToken();
    
    // Prepare headers
    const headers: { [key: string]: string } = {};
    if (jwtToken) {
      headers['Authorization'] = `Bearer ${jwtToken}`;
    }
    
    const apiUrl = `${baseUrl}/api/scraper/punk-bids/${bidId}?withDetails=true`;
    return this.http.get<any>(apiUrl, { headers: Object.keys(headers).length > 0 ? headers : undefined }).pipe(
      tap(() => console.log(`[DataService] Bid details with punk indices API call started for bidId: ${bidId}`)),
      timeout(10000), // 10 seconds timeout
      tap((res: any) => {
        console.log(`[DataService] Bid details with punk indices API completed for bidId: ${bidId}`);
      }),
      catchError((error) => {
        console.error('Error fetching bid details with punk indices:', error);
        return of(null);
      })
    );
  }

  /**
   * Fetch all stash bids from the order book
   * @param params Optional parameters for sorting and filtering
   * @returns Observable with stash bids (pending only unless includeAllStatuses is true)
   */
  fetchAllStashBids(params?: {
    sortBy?: string;
    sortOrder?: string;
    filterBidder?: string;
    filterSource?: string;
    filterAmountMin?: number;
    filterAmountMax?: number;
    filterExpiresFrom?: string;
    filterExpiresTo?: string;
    filterConfidential?: boolean;
    filterPunkIds?: number[];
    /** Default "any". Use "all" when the bid must cover every listed punk (or be a collection bid). */
    filterPunkIdsMatch?: 'any' | 'all';
    /** Inclusive ranges merged with filterPunkIds, e.g. "0-9999,42" (shorter than huge ID lists). */
    filterPunkIdRanges?: string;
    /** When true, requests all bid statuses from the API (e.g. archived / non-pending). */
    includeAllStatuses?: boolean;
    /** Server-persisted UI bid category (trait | collection | custom | owner). */
    filterMarketBidType?: string;
    /** Optional status filter (supports pending/invalid and UI aliases active/expired/cancelled/unfunded). */
    filterStatus?: string;
    /** When true, exclude bids where stash_funded is false. */
    excludeUnfunded?: boolean;
    page?: number;
    limit?: number;
  }): Observable<{ bids: any[]; lastBidFetch: Date | null; total: number }> {
    console.log(`[DataService] fetchAllStashBids started`);
    // Extract base URL from alternativeListingsGraphURI
    const graphUri = environment.alternativeListingsGraphURI || '';
    const baseUrl = graphUri.replace(/\/graphql$/, '');
    
    // Determine source filter based on environment collection if not provided
    const getBidSource = (collection: string): string => {
      if (collection === 'cryptopunks') return 'cryptopunks.eth.limo,cryptopunks.app';
      if (collection === 'cryptopunks-v1') return 'v1cryptopunks.com';
      return 'cryptopunks.eth.limo'; // fallback
    };
    
    // Handle filterSource logic:
    // - If filterSource is undefined (not set): use default sources (app + eth.limo for cryptopunks)
    // - If filterSource is empty string ("All Sources" selected): use default sources (app + eth.limo for cryptopunks)
    // - If filterSource has a value: filter only on that specific source
    if (params?.filterSource === undefined || (typeof params.filterSource === 'string' && params.filterSource.trim() === '')) {
      // Not set or "All Sources" selected: use default sources for collection
      params = { ...params, filterSource: getBidSource(environment.collection) };
    }
    
    // Build query string from parameters
    const queryParams: string[] = [];
    if (params?.sortBy) {
      queryParams.push(`sortBy=${encodeURIComponent(params.sortBy)}`);
    }
    if (params?.sortOrder) {
      queryParams.push(`sortOrder=${encodeURIComponent(params.sortOrder)}`);
    }
    if (params?.filterBidder) {
      queryParams.push(`filterBidder=${encodeURIComponent(params.filterBidder)}`);
    }
    if (params?.filterSource) {
      queryParams.push(`filterSource=${encodeURIComponent(params.filterSource)}`);
    }
    if (params?.filterAmountMin !== undefined && params?.filterAmountMin !== null) {
      queryParams.push(`filterAmountMin=${encodeURIComponent(params.filterAmountMin.toString())}`);
    }
    if (params?.filterAmountMax !== undefined && params?.filterAmountMax !== null) {
      queryParams.push(`filterAmountMax=${encodeURIComponent(params.filterAmountMax.toString())}`);
    }
    if (params?.filterExpiresFrom) {
      queryParams.push(`filterExpiresFrom=${encodeURIComponent(params.filterExpiresFrom)}`);
    }
    if (params?.filterExpiresTo) {
      queryParams.push(`filterExpiresTo=${encodeURIComponent(params.filterExpiresTo)}`);
    }
    if (params?.filterConfidential !== undefined && params?.filterConfidential !== null) {
      queryParams.push(`filterConfidential=${encodeURIComponent(params.filterConfidential.toString())}`);
    }
    if (params?.filterPunkIds && params.filterPunkIds.length > 0) {
      const punkIdsString = params.filterPunkIds.join(',');
      queryParams.push(`filterPunkIds=${encodeURIComponent(punkIdsString)}`);
    }
    if (params?.filterPunkIdsMatch === 'all') {
      queryParams.push(`filterPunkIdsMatch=${encodeURIComponent('all')}`);
    }
    if (params?.filterPunkIdRanges?.trim()) {
      queryParams.push(`filterPunkIdRanges=${encodeURIComponent(params.filterPunkIdRanges.trim())}`);
    }
    if (params?.includeAllStatuses === true) {
      queryParams.push('includeAllStatuses=true');
    }
    if (params?.filterMarketBidType?.trim()) {
      queryParams.push(
        `filterMarketBidType=${encodeURIComponent(params.filterMarketBidType.trim())}`,
      );
    }
    if (params?.filterStatus?.trim()) {
      queryParams.push(`filterStatus=${encodeURIComponent(params.filterStatus.trim())}`);
    }
    if (params?.excludeUnfunded === true) {
      queryParams.push('excludeUnfunded=true');
    }
    if (params?.page != null && params.page >= 1) {
      queryParams.push(`page=${params.page}`);
    }
    if (params?.limit != null && params.limit >= 1) {
      queryParams.push(`limit=${params.limit}`);
    }
    
    const queryString = queryParams.length > 0 ? `?${queryParams.join('&')}` : '';
    const apiUrl = `${baseUrl}/api/scraper/punk-bids/all${queryString}`;
    
    const headers = this.buildAuthHeaders();
    
    return this.http.get<any>(apiUrl, { headers: Object.keys(headers).length > 0 ? headers : undefined }).pipe(
      tap(() => console.log(`[DataService] All stash bids API call started`)),
      timeout(30000), // 30 seconds timeout
      map((res: any) => {
        console.log(`[DataService] All stash bids API completed, bids count:`, res?.bids?.length || res?.length || 0);
        // Handle different response structures
        const bids = res?.bids || res || [];
        const includeAll = params?.includeAllStatuses === true;
        const unfundedFilter =
          (params?.filterStatus || '').trim().toLowerCase() === 'unfunded';
        const filteredBids =
          includeAll || unfundedFilter
            ? bids
            : bids.filter((bid: any) => bid.status === 'pending');
        const total =
          typeof res?.total === 'number' && !Number.isNaN(res.total)
            ? res.total
            : filteredBids.length;
        // Return bids, lastBidFetch, and total (full count for server-side pagination)
        return {
          bids: filteredBids,
          lastBidFetch: res?.lastBidFetch || null,
          total,
        };
      }),
      catchError((error) => {
        console.error('Error fetching all stash bids:', error);
        return of({ bids: [], lastBidFetch: null, total: 0 });
      })
    );
  }

  fetchPunksmarketBids(params?: FetchPunksmarketBidsParams) {
    return this.punksmarketBidIndexer.fetchBids(params ?? {});
  }

  fetchPunksmarketBidsForPunk(punkId: string | number, limit = 200) {
    return this.punksmarketBidIndexer.fetchBidsMatchingPunk(punkId, limit);
  }

  fetchPunksmarketBidById(bidId: string) {
    return this.punksmarketBidIndexer.fetchBidById(bidId);
  }

  /**
   * Fetch punk bids from the listings indexer with no status filter (any status: pending, invalid, etc.).
   * Used by the status page to display bids in the indexer. Supports server-side pagination, filtering, and sorting.
   * @param page 1-based page number (optional)
   * @param limit Page size (optional)
   * @param filters Optional server-side filter/sort params
   * @returns Observable with bids (current page), total count, and lastBidFetch
   */
  fetchAllBidsNoStatusFilter(
    page?: number,
    limit?: number,
    filters?: { sortBy?: string; sortOrder?: string; filterStatus?: string; filterSource?: string; filterBidder?: string }
  ): Observable<{ bids: any[]; total: number; lastBidFetch: Date | null }> {
    const graphUri = environment.alternativeListingsGraphURI || '';
    const baseUrl = graphUri.replace(/\/graphql$/, '');
    const params = new URLSearchParams();
    params.set('includeAllStatuses', 'true');
    if (page != null && page >= 1) params.set('page', String(page));
    if (limit != null && limit >= 1) params.set('limit', String(limit));
    if (filters?.sortBy) params.set('sortBy', filters.sortBy);
    if (filters?.sortOrder) params.set('sortOrder', filters.sortOrder);
    if (filters?.filterStatus) params.set('filterStatus', filters.filterStatus);
    if (filters?.filterSource) params.set('filterSource', filters.filterSource);
    if (filters?.filterBidder) params.set('filterBidder', filters.filterBidder);
    const apiUrl = `${baseUrl}/api/scraper/punk-bids/all?${params.toString()}`;

    return this.http.get<any>(apiUrl).pipe(
      timeout(20000),
      map((res: any) => ({
        bids: res?.bids || res || [],
        total: res?.total ?? (res?.bids?.length ?? res?.length ?? 0),
        lastBidFetch: res?.lastBidFetch ?? null
      })),
      catchError((error) => {
        console.error('[DataService] Error fetching all bids (no status filter):', error);
        return of({ bids: [], total: 0, lastBidFetch: null });
      })
    );
  }

  /**
   * Admin-only endpoint: returns paginated rows from external_orderbook_bid_publications.
   */
  /**
   * Register a UI-initiated on-chain tx so listing notifications can show our marketplace label.
   * Fire-and-forget: failures are logged and do not block the user flow.
   */
  registerUiTransactionOrigin(payload: {
    txHash: string;
    walletAddress: string;
    tokenId: string;
    action: string;
    displaySource: string;
    submittedAtBlock: number;
    confirmedBlock: number;
    chainId: number;
    amountEth?: number;
  }): Observable<{ success: boolean }> {
    const graphUri = environment.alternativeListingsGraphURI || '';
    const baseUrl = graphUri.replace(/\/graphql$/, '');
    if (!baseUrl) {
      return of({ success: false });
    }

    const apiUrl = `${baseUrl}/api/scraper/ui-transaction-origins`;
    return this.http.post(apiUrl, payload).pipe(
      timeout(10000),
      map(() => ({ success: true })),
      catchError((error) => {
        console.warn('[DataService] Failed to register UI transaction origin:', error);
        return of({ success: false });
      }),
    );
  }

  fetchExternalOrderbookBidPublications(
    page: number = 1,
    limit: number = 20,
    source: string = 'cryptopunks.app',
  ): Observable<ExternalOrderbookBidPublicationsPage> {
    const graphUri = environment.alternativeListingsGraphURI || '';
    const baseUrl = graphUri.replace(/\/graphql$/, '');
    const params = new URLSearchParams();
    params.set('page', String(Math.max(1, Math.floor(page))));
    params.set('limit', String(Math.max(1, Math.floor(limit))));
    if (source?.trim()) {
      params.set('source', source.trim());
    }

    const apiUrl = `${baseUrl}/api/scraper/external-orderbook-bid-publications?${params.toString()}`;
    const headers = this.buildAuthHeaders();

    return this.http
      .get<ExternalOrderbookBidPublicationsPage>(apiUrl, {
        headers: Object.keys(headers).length > 0 ? headers : undefined,
      })
      .pipe(
        timeout(20000),
        map((res) => ({
          items: Array.isArray(res?.items) ? res.items : [],
          total: typeof res?.total === 'number' ? res.total : 0,
          page: typeof res?.page === 'number' ? res.page : Math.max(1, Math.floor(page)),
          limit: typeof res?.limit === 'number' ? res.limit : Math.max(1, Math.floor(limit)),
          totalPages: typeof res?.totalPages === 'number' ? res.totalPages : 0,
        })),
        catchError((error) => {
          console.error('[DataService] Error fetching external orderbook bid publications:', error);
          return of({
            items: [],
            total: 0,
            page: Math.max(1, Math.floor(page)),
            limit: Math.max(1, Math.floor(limit)),
            totalPages: 0,
          });
        }),
      );
  }

  /**
   * Admin-only: paginated stash bid protection audit log from listings-scraper.
   */
  fetchStashBidProtectionLogs(
    page: number = 1,
    limit: number = 20,
    filters?: {
      status?: string;
      reason?: string;
      legacyPunkIndex?: number;
      protectedBidId?: string;
      from?: string;
      to?: string;
    },
  ): Observable<StashBidProtectionLogsPage> {
    const graphUri = environment.alternativeListingsGraphURI || '';
    const baseUrl = graphUri.replace(/\/graphql$/, '');
    const params = new URLSearchParams();
    params.set('page', String(Math.max(1, Math.floor(page))));
    params.set('limit', String(Math.max(1, Math.floor(limit))));
    if (filters?.status?.trim()) params.set('status', filters.status.trim());
    if (filters?.reason?.trim()) params.set('reason', filters.reason.trim());
    if (filters?.protectedBidId?.trim()) params.set('protectedBidId', filters.protectedBidId.trim());
    if (filters?.legacyPunkIndex !== undefined && !Number.isNaN(filters.legacyPunkIndex)) {
      params.set('legacyPunkIndex', String(filters.legacyPunkIndex));
    }
    if (filters?.from?.trim()) params.set('from', filters.from.trim());
    if (filters?.to?.trim()) params.set('to', filters.to.trim());

    const apiUrl = `${baseUrl}/api/scraper/stash-bid-protection-logs?${params.toString()}`;
    const headers = this.buildAuthHeaders();

    return this.http
      .get<StashBidProtectionLogsPage>(apiUrl, {
        headers: Object.keys(headers).length > 0 ? headers : undefined,
      })
      .pipe(
        timeout(20000),
        map((res) => ({
          enabled: res?.enabled === true,
          items: Array.isArray(res?.items) ? res.items : [],
          total: typeof res?.total === 'number' ? res.total : 0,
          page: typeof res?.page === 'number' ? res.page : Math.max(1, Math.floor(page)),
          limit: typeof res?.limit === 'number' ? res.limit : Math.max(1, Math.floor(limit)),
          totalPages: typeof res?.totalPages === 'number' ? res.totalPages : 0,
        })),
        catchError((error) => {
          console.error('[DataService] Error fetching stash bid protection logs:', error);
          return of({
            enabled: false,
            items: [],
            total: 0,
            page: Math.max(1, Math.floor(page)),
            limit: Math.max(1, Math.floor(limit)),
            totalPages: 0,
          });
        }),
      );
  }

  ///////////////////////////////////////////////////////////////////////////////
  // USD ////////////////////////////////////////////////////////////////////////
  ///////////////////////////////////////////////////////////////////////////////

  fetchUSDPrice() {
    console.log(`[DataService] fetchUSDPrice started`);

    // Don't run on server
    if (this.platformService.isServer) {
      console.log(`[DataService] fetchUSDPrice skipped on server`);
      return;
    }

    if (environment.featureFlags?.NEW_ERC20_PAIR_DATASOURCE) {
      this.fetchAlchemyPriceBySymbol('ETH', 'USD').pipe(
        tap(() => console.log(`[DataService] Alchemy USD (ETH) price call started`)),
        timeout(3000),
        tap((price) => console.log(`[DataService] Alchemy USD (ETH) price completed:`, price)),
        tap((price) => this.usd.next(price)),
        catchError((error) => {
          console.error('[DataService] Error fetching USD price from Alchemy:', error);
          if (error.name === 'TimeoutError') {
            console.warn('[DataService] Alchemy USD price timeout, using default value');
          }
          this.usd.next(0);
          return of(0);
        })
      ).subscribe();
      return;
    }

    this.http.get('https://punkcdn.com/data/coin-price', {
      params: { id: 'ethereum' }
    }).pipe(
      tap(() => console.log(`[DataService] USD price API call started`)),
      timeout(3000), // 3 seconds timeout for SSR
      tap((res: any) => {
        console.log(`[DataService] USD price API completed, price:`, res?.current_price || 0);
      }),
      map((res: any) => res?.current_price || 0),
      tap((res) => this.usd.next(res)),
      catchError((error) => {
        console.error('[DataService] Error fetching USD price:', error);
        if (error.name === 'TimeoutError') {
          console.warn('[DataService] USD price timeout reached, using default value');
        }
        this.usd.next(0);
        return of(0);
      })
    ).subscribe();
  }

  fetchBTCPrice() {
    console.log(`[DataService] fetchBTCPrice started`);

    // Don't run on server
    if (this.platformService.isServer) {
      console.log(`[DataService] fetchBTCPrice skipped on server`);
      return;
    }

    if (environment.featureFlags?.NEW_ERC20_PAIR_DATASOURCE) {
      this.fetchAlchemyPriceBySymbol('BTC', 'USD').pipe(
        tap(() => console.log(`[DataService] Alchemy BTC price call started`)),
        timeout(3000),
        tap((price) => console.log(`[DataService] Alchemy BTC price completed:`, price)),
        tap((price) => this.btc.next(price)),
        catchError((error) => {
          console.error('[DataService] Error fetching BTC price from Alchemy:', error);
          if (error.name === 'TimeoutError') {
            console.warn('[DataService] Alchemy BTC price timeout, using default value');
          }
          this.btc.next(0);
          return of(0);
        })
      ).subscribe();
      return;
    }

    this.http.get('https://punkcdn.com/data/coin-price', {
      params: { id: 'bitcoin' }
    }).pipe(
      tap(() => console.log(`[DataService] BTC price API call started`)),
      timeout(10000), // 3 seconds timeout for SSR
      tap((res: any) => {
        console.log(`[DataService] BTC price API completed, price:`, res?.current_price || 0);
      }),
      map((res: any) => res?.current_price || 0),
      tap((res) => this.btc.next(res)),
      catchError((error) => {
        console.error('[DataService] Error fetching BTC price:', error);
        if (error.name === 'TimeoutError') {
          console.warn('[DataService] BTC price timeout reached, using default value');
        }
        this.btc.next(0);
        return of(0);
      })
    ).subscribe();
  }

  private fetchAlchemyPriceBySymbol(symbol: string, currency: string = 'USD'): Observable<number> {
    const baseUrl = `https://api.g.alchemy.com/prices/v1/${environment.alchemyApiKey}/tokens/by-symbol`;
    return this.http.get(baseUrl, {
      params: { symbols: symbol, currency }
    }).pipe(
      tap((res) => console.log('[DataService] Alchemy price raw response:', res)),
      map((res: any) => {
        const prices = res?.data?.[0]?.prices;
        if (Array.isArray(prices) && prices.length) {
          const desired = prices.find((p: any) => String(p?.currency || '').toUpperCase() === String(currency).toUpperCase());
          const candidate = desired ?? prices[0];
          const value = Number(candidate?.value);
          if (!Number.isNaN(value) && value > 0) return value;
        }
        return 0;
      }),
    );
  }

  getUSDPrice(): number {
    return this.usd.getValue();
  }

  getBTCPrice(): number {
    return this.btc.getValue();
  }

  ///////////////////////////////////////////////////////////////////////////////
  // NFTX ///////////////////////////////////////////////////////////////////////
  ///////////////////////////////////////////////////////////////////////////////

  fetchNftxVault(limit: number): Observable<any> {
    console.log(`[DataService] fetchNftxVault started with limit: ${limit}`);
    
    // Don't run on server
    if (this.platformService.isServer) {
      console.log(`[DataService] fetchNftxVault skipped on server`);
      return of([]);
    }

    // CryptoPunks V1: there is no NFTX pool; never attempt to load NFTX data.
    if (environment.collection === 'cryptopunks-v1') {
      console.log('[DataService] fetchNftxVault skipped for cryptopunks-v1 (no NFTX pool)');
      return of([]);
    }
    
    let vault: any;
    const contract = environment.nftxVaultAddress
    return this.apollo.use('nftx').query({
      query: GET_NFTX_VAULT,
      variables: { contract, limit },
    }).pipe(
      tap(() => console.log(`[DataService] NFTX GraphQL query started`)),
      timeout(8000), // 8 seconds timeout for NFTX
      tap((res: any) => {
        console.log(`[DataService] NFTX GraphQL query completed`);
        vault = res.data.vault;
      }),
      switchMap(() => this.fetchNftxPrice()),
      map((res) => {
        console.log(`[DataService] NFTX processing completed, holdings count:`, vault.holdings?.length || 0);
        return vault.holdings.map((item: any) => ({
          id: item.tokenId,
          listing: {
            id: item.tokenId,
            value: res,
            blockTimestamp: item.dateAdded,
            source: {
              id: contract,
              domain: 'nftx.io',
              name: 'nftx.io',
              icon: 'https://v2.nftx.io/images/logo_on_black.svg',
              url: `https://v2.nftx.io/vault/${contract}/${item.tokenId}`,
              internal: limit > 1 ? null : ['/', 'nftx-vault'],
            },
          },
        } as Punk));
      }),
      switchMap((res: Punk[]) => this.addAttributes(res)),
      catchError((error) => {
        console.error('[DataService] Error fetching NFTX data:', error);
        if (error.name === 'TimeoutError') {
          console.warn('[DataService] NFTX timeout reached, returning empty array');
        }
        return of([]);
      }),
    );
  }


  fetchCurrentAuction(): Observable<Auction|null> {
    if (!this.apollo) {
      console.warn('[DataService] Apollo not available for fetchCurrentAuction');
      return of(null);
    }
    if (!environment.auctionHouseUri) {
      console.warn('[DataService] Auction House URI not available for fetchCurrentAuction');
      return of(null);
    }

    console.log('[DataService] fetchCurrentAuction started, environment:', environment.collection);
    console.log('[DataService] Auction House URI:', environment.auctionHouseUri);
    
    const query = gql`
      {
        auctions(first:1, orderBy: blockNumber, orderDirection: desc) {
          id
          phunk {
            id
          }
          bids {
            id
          }
          amount
          startTime
          endTime
          settled,
          bidder {
            id
          }
        }
      }`;

    console.log('[DataService] Apollo query:', query);

    // Use named Apollo client for auctions (auctionHouseUri)
    return this.apollo.use('auctionHouse').query({ query }).pipe(
              timeout(this.platformService.isServer ? 15000 : 30000), // Longer timeout on client side
      tap((res: any) => {
                  // Complete Apollo response log
        // console.log('[DataService] Apollo RAW response:', JSON.stringify(res, null, 2));
        if (res.errors) {
          console.error('[DataService] Apollo response GraphQL errors:', res.errors);
        }
        if (res.error) {
          console.error('[DataService] Apollo response error:', res.error);
        }
        /*
        console.log('[DataService] Apollo response received:', {
          hasData: !!res.data,
          hasAuctions: !!res.data?.auctions,
          auctionsLength: res.data?.auctions?.length || 0,
          firstAuction: res.data?.auctions?.[0] || null
        });
        */
      }),
      map((res: any) =>  (res.data && res.data.auctions && res.data.auctions.length > 0 ? res.data.auctions[0] : {}) as Auction),
      tap((res:Auction) => {
        // console.log('[DataService] Mapped auction result:', res);
      }),
      catchError((error) => {
                  // Detailed Apollo error log
        console.error('[DataService] fetchCurrentAuction error:', {
          name: error.name,
          message: error.message,
          status: error.status,
          networkError: error.networkError,
          graphQLErrors: error.graphQLErrors,
          response: error.response,
          result: error.result,
          body: error.body,
          error: error.error
        });
        if (error.name === 'TimeoutError') {
          console.warn('Timeout reached, returning empty auction');
        } else {
          console.error('Error fetching data:', error);
        }
        return of(null);
      }),
    );
  }

  fetchGondiListings(limit: number): Observable<any> {
    // Don't run on server
    if (this.platformService.isServer) {
      return of([]);
    }
    
    if (!environment.gondiEnabled) return of([])
    const collections = environment.collection === 'cryptopunks' ? [2836, 12530] : [12638]
    
    // Add cache-busting parameter
    const cacheBuster = Date.now();
    const context = {
      uri: `${environment.gondiUrl}?environment=${environment.collection}`
    };
    
    console.log('🔍 Gondi request:', {
      url: context.uri,
      collections,
      gondiEnabled: environment.gondiEnabled,
      gondiUrl: environment.gondiUrl
    });
    
    return this.apollo.query({
      query: GET_GONDI_LISTINGS,
      variables: { collections, onlyListed: true, marketplaces: ["NATIVE"] },
      context
    }).pipe(
      timeout(1000),
      tap((res: any) => {
        console.log('✅ Gondi response:', {
          hasData: !!res.data,
          hasNfts: !!res.data?.nfts,
          edgesCount: res.data?.nfts?.edges?.length || 0,
          totalCount: res.data?.nfts?.totalCount || 0
        });
      }),
      map((res: any) => {
        return res.data.nfts.edges.map((item: any) => {

          const converted = item.node.nftPriceSample.currencyAddress === '0xa0b86991c6218b36c1d19d4a2e9eb0ce3606eb48' 
            ? Math.round(parseFloat(item.node.nftPriceSample.value) / this.usd.value) * 1000000000000
            : 1 * parseFloat(item.node.nftPriceSample.value)
          return {
            id: item.node.tokenId,
            listing: {
              id: item.node.tokenId,
              value: `${converted}`,
              source: {
                id: 'contract',
                domain: 'gondi.xyz',
                name: 'gondixyz',
                icon: '/assets/gondi.svg',
                url: `https://gondi.xyz/collections/${item.node.collection.slug}/${item.node.tokenId}`,
              },
            },
          } as Punk;
        });
      }),
      switchMap((res: Punk[]) => this.addAttributes(res)),
      tap((res) => {
        res.forEach(p => this.gondiListings.set(p.id, p.listing!))
      }),
      //tap((res) => console.log('GONDI!', res)),
      catchError((error) => {
        console.error('❌ Gondi error:', {
          name: error.name,
          message: error.message,
          networkError: error.networkError,
          graphQLErrors: error.graphQLErrors
        });
        
        if (error.name === 'TimeoutError') {
          console.warn('⏰ Gondi timeout reached, returning empty array');
        } else {
          console.error('💥 Gondi error fetching data:', error);
        }
        return of([]);
      }),
    );
  }

  fetchNftxVaultListing(id: string): Observable<any> {
    // Don't run on server
    if (this.platformService.isServer) {
      return of(null);
    }

    // CryptoPunks V1: there is no NFTX pool; never attempt to load NFTX data.
    if (environment.collection === 'cryptopunks-v1') {
      return of(null);
    }
    
    const contract = environment.nftxVaultAddress
    return this.fetchNftxPrice().pipe(
      switchMap((price) => {
        return this.apollo.query({
          query: GET_SIGNLE_NFTX_VAULT,
          variables: { contract, punkId: id },
        }).pipe(
          map((res: any) => res.data.vault.holdings[0] || null),
          map((res) => (res ? {
            id: res.tokenId,
            value: price,
            blockTimestamp: res.dateAdded,
            source: {
              id: contract,
              domain: 'nftx.io',
              name: 'nftx.io',
              icon: 'https://v2.nftx.io/images/logo_on_black.svg',
              url: `https://v2.nftx.io/vault/${contract}/${id}`,
            },
          } as Listing : null)),
        );
      }),
      catchError((error) => {
        console.log('Error fetching data:', error);
        return of(null);
      }),
    );
  }

  fetchNftxPrice() {
    console.log(`[DataService] fetchNftxPrice started`);
    
    // Don't run on server
    if (this.platformService.isServer) {
      console.log(`[DataService] fetchNftxPrice skipped on server`);
      return of('0');
    }

    // Use custom scraper when enabled
    if (environment.featureFlags?.USE_CUSTOM_NFTX_SCRAPER && environment.nftxCustomScraperBaseURL) {
      const endpoint = `${environment.nftxCustomScraperBaseURL}${environment.wrappedPunksAddress}`;
      console.log('[DataService] Using custom NFTX scraper endpoint:', endpoint);
      return this.http.get(endpoint).pipe(
        tap(() => console.log(`[DataService] Custom NFTX scraper API call started`)),
        timeout(3000),
        tap((res: any) => {
          console.log(`[DataService] Custom NFTX scraper API completed`);
          console.log(`[DataService] Custom NFTX scraper response price:`, res?.price);
        }),
        map((res: any) => {
          const ethValue = Number(res?.price) || 0;
          const weiValue = BigInt(Math.round(ethValue * 1e18));
          return (weiValue).toString();
        }),
        catchError((error) => {
          console.error('[DataService] Error fetching NFTX price from custom scraper:', error);
          if (error.name === 'TimeoutError') {
            console.warn('[DataService] Custom NFTX scraper timeout reached, returning 0');
          }
          return of('0');
        }),
      );
    }

    // Fallback to CoinGecko
    const key = 'CG-ABw3YXpW7YAFzQE7GbLaiZkp'; // <— secure that
    const url = 'https://pro-api.coingecko.com/api/v3/simple/price';
    const collection = environment.collection === 'cryptophunks' ? 'phunk-vault-nftx' : 'punk-vault-nftx'
    console.log('[DataService] fetchNftxPrice collection:', collection);

    const headers = { 'x-cg-pro-api-key': key };
    const params = {
      ids: collection,
      vs_currencies: 'eth',
    };

    return this.http.get(url, { params, headers }).pipe(
      tap(() => console.log(`[DataService] NFTX price API call started`)),
      timeout(3000), // 3 seconds timeout for SSR
      tap((res: any) => {
        console.log(`[DataService] NFTX price API completed`);
        const ethValue = res[collection]?.['eth'] || 0;
        console.log(`[DataService] NFTX price ETH value:`, ethValue);
      }),
      map((res: any) => {
        const ethValue = res[collection]?.['eth'] || 0;
        const weiValue = BigInt(Math.round(ethValue * 1e18));
        const fees = BigInt(Math.round((ethValue * .06) * 1e18));
        return (weiValue + fees).toString();
      }),
      catchError((error) => {
        console.error('[DataService] Error fetching NFTX price:', error);
        if (error.name === 'TimeoutError') {
          console.warn('[DataService] NFTX price timeout reached, returning 0');
        }
        return of('0');
      }),
    );
  }

  ///////////////////////////////////////////////////////////////////////////////
  // META CARDS /////////////////////////////////////////////////////////////////
  ///////////////////////////////////////////////////////////////////////////////

  async fetchPunkCard(punkId: string, param: number): Promise<void> {
    // Don't run on server
    if (this.platformService.isServer) {
      return;
    }
    
    await firstValueFrom(
      // this.http.get(`http://localhost:3200/${punkId}?=${Date.now()}`, {
      this.http.get(`${environment.siteUrl}/${punkId}?=${param}`, {
        responseType: 'text',
        headers: { 'cryptopunks': 'true' },
      }).pipe(
        timeout(5000), // 5 seconds timeout for SSR
        map((res: any) => !!res),
      )
    );
  }

  async fetchFloorPunksCard(param: number): Promise<void> {
    // Don't run on server
    if (this.platformService.isServer) {
      return;
    }
    
    await firstValueFrom(
      // this.http.get(`http://localhost:3200/floorpunks?=${Date.now()}`, {
      this.http.get(`https://punks.limo/floorpunks?=${param}`, {
        responseType: 'text',
        headers: { 'cryptopunks': 'true' },
      }).pipe(
        timeout(5000), // 5 seconds timeout for SSR
        map((res: any) => !!res),
      )
    );
  }

  async fetchUserCard(address: string, param: number): Promise<void> {
    // Don't run on server
    if (this.platformService.isServer) {
      return;
    }
    
    await firstValueFrom(
      // this.http.get(`http://localhost:3200/floorpunks?=${Date.now()}`, {
      this.http.get(`https://punks.limo/${address}?=${param}`, {
        responseType: 'text',
        headers: { 'cryptopunks': 'true' },
      }).pipe(
        timeout(5000), // 5 seconds timeout for SSR
        map((res: any) => !!res),
      )
    );
  }

  fetchCigCEO(): Observable<number | null> {
    const alchemyUrl = `https://eth-mainnet.g.alchemy.com/v2/${environment.alchemyApiKey}`;
    
    const data = encodeFunctionData({
      abi: CigABI,
      functionName: 'CEO_punk_index',
      args: []
    });

    return this.http.post(alchemyUrl, {
      jsonrpc: '2.0',
      id: 1,
      method: 'eth_call',
      params: [
        {
          to: environment.cigAddress,
          data
        },
        'latest'
      ]
    }).pipe(
      map((response: any) => {
        if (response && 'result' in response) {
          const result = response.result as string;
          if (result && result !== '0x') {
            return parseInt(result, 16);
          }
        }
        return null;
      }),
      catchError((error) => {
        console.error('Error fetching CIG CEO:', error);
        return of(null);
      })
    );
  }
}
